// src/stores/taskStore.js - Updated with section support and simplified error handling
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import * as taskService from '../services/taskService';
import * as sectionService from '../services/sectionService';
import useAuthStore from './authStore';
// Import the enhanced task service methods
import {
  createTaskWithContent,
  linkContentToTask,
  getTaskContent
} from '../services/taskService';
import { uploadContentFiles } from '../utils/fileUploadUtils';
import contentService from '../services/contentService';
import globalTaskCalendarService from '../services/globalTaskCalendarService';
import { TASKS_PER_SECTION } from '../components/tasks/taskConstants';
import { sortTasksByOrder, debugTaskOrdering } from '../utils/taskOrderingUtils';

// Add these functions to the taskStore create function
// Inside the create callback, add:

// Import api service - with fallback if it doesn't exist
let api;
try {
  api = require('../services/api').api;
} catch (error) {
  // API service might not be available - create a dummy object
  console.warn('API service not available, cache invalidation will be disabled');
  api = {
    invalidateCache: () => { } // No-op function
  };
}

/**
 * Enhanced Task Store with section support
 * - Manages tasks, sections, and their relationships
 * - Provides CRUD operations for both tasks and sections
 * - Supports optimistic updates with rollback on error
 */
const useTaskStore = create(
  devtools(
    persist(
      (set, get) => ({
        // State
        tasks: [],
        myTasks: [],
        projectTasks: {}, // Indexed by projectId - for Kanban/List (section-based)
        gridTasks: {}, // Indexed by projectId - for Grid view (all tasks)
        calendarTasks: {}, // Indexed by projectId-dateRange (e.g., "proj123-2025-06-01-2025-06-30")
        calendarCache: {}, // Cache metadata for calendar views
        globalCalendarSummary: {}, // Summary data for global calendar
        globalCalendarTasks: {}, // Detailed tasks by date for global calendar
        taskSubtasks: {}, // Indexed by taskId
        currentTask: null,
        taskCreateContext: {},
        isDragging: false, // Track drag state
        // Section state
        sections: [],
        projectSections: {}, // Indexed by projectId
        currentSection: null,
        sectionPagination: {}, // { sectionId: { page: 1, hasMore: true, totalTasks: 0 } }
        isLoading: false,
        criticalError: null,        // Only for task loading failures
        operationError: null,       // For non-critical operations
        
        // Optimistic update tracking
        pendingOperations: new Map(), // Map<operationId, { type, timestamp, data }>
        lastSyncTimestamp: Date.now(),
        optimisticUpdates: new Map(), // Map<taskId, { operation, timestamp }>
        lastUpdate: Date.now(), // Force re-renders on updates
        totalTasks: 0,
        pagination: {
          page: 1,
          totalPages: 1,
          limit: 10
        },

        // Task drawer state
        isTaskDrawerOpen: false,
        taskDrawerContext: null, // Store additional context like hideProjectField
        
        // Drag state management
        setIsDragging: (isDragging) => set({ isDragging }),
        
        openTaskDrawer: (taskOrTaskID, context = {}) => {
          console.log('openTaskDrawer called with:', taskOrTaskID);
          
          // Check if this is a create operation (object with no _id)
          const isCreateMode = taskOrTaskID &&
            typeof taskOrTaskID === 'object' &&
            !taskOrTaskID._id;

          // Get taskId only if not in create mode
          const taskId = !isCreateMode ? (taskOrTaskID?._id || taskOrTaskID) : null;
          
          console.log('openTaskDrawer - parsed values:', {
            isCreateMode,
            taskId,
            isObject: typeof taskOrTaskID === 'object',
            hasId: taskOrTaskID?._id
          });

          // Clear any previous errors
          set({ criticalError: null, operationError: null });

          // Open the drawer
          set({
            isTaskDrawerOpen: true,
            // Set currentTask to null for create mode, or the passed task object if available
            // When passing a string ID, clear currentTask to null to indicate loading
            currentTask: isCreateMode ? null : (
              typeof taskOrTaskID === 'object' && taskOrTaskID._id ? taskOrTaskID : 
              typeof taskOrTaskID === 'string' ? null : get().currentTask
            ),
            taskDrawerContext: context, // Store the drawer context
          });

          // For create mode, store the context information but don't fetch
          if (isCreateMode) {
            // Store the context in a separate property
            set({
              taskCreateContext: taskOrTaskID,
              isLoading: false
            });
            console.log("Opening drawer in create mode with context:", taskOrTaskID);
          }
          // For edit mode with existing task already loaded
          else if (get().currentTask?._id === taskId) {
            // Force fetch to get fresh timer data
            console.log("Task already loaded, but fetching fresh data for timers:", taskId);
            set({ isLoading: true });
            
            get().fetchTask(taskId)
              .then((task) => {
                if (task) {
                  set({ isLoading: false });
                } else {
                  set({
                    isLoading: false,
                  });
                }
              })
              .catch(error => {
                console.error("Error fetching task:", error);
                set({
                  isLoading: false,
                  operationError: error?.message || 'Failed to load task'
                });
              });
          }
          // For edit mode with task not loaded
          else if (taskId) {
            // Load the task
            set({ isLoading: true });
            console.log("Fetching task:", taskId);

            get().fetchTask(taskId)
              .then((task) => {
                if (task) {
                  set({ isLoading: false });
                } else {
                  set({
                    isLoading: false,
                    // Don't set error here - task might be null but still fetched successfully
                  });
                }
              })
              .catch(error => {
                console.error("Error fetching task:", error);
                set({
                  isLoading: false,
                  operationError: error?.message || 'Failed to load task'
                });
              });
          }
        },
        // Close the task drawer
        // closeTaskDrawer: () => set({ isTaskDrawerOpen: false, currentTask: null }),
        closeTaskDrawer: () => set({
          isTaskDrawerOpen: false,
          currentTask: null,
          taskCreateContext: null, // Clear create context
          taskDrawerContext: null, // Clear drawer context
          operationError: null // Clear any errors
        }),
        // Helper Methods
        clearError: () => set({ criticalError: null, operationError: null }),

        setLoading: (isLoading) => set({ isLoading }),

        // SECTION-RELATED METHODS

        // Fetch sections for a project
        fetchProjectSections: async (projectId) => {
          set({ isLoading: true, criticalError: null });

          try {
            const response = await sectionService.getProjectSections(projectId);
            console.log("🚀 ~ fetchProjectSections: ~ response:", response)

            set(state => ({
              projectSections: {
                ...state.projectSections,
                [projectId]: response.sections
              },
              isLoading: false
            }));

            return response.sections;
          } catch (error) {
            // Using simplified error handling approach - API will standardize errors
            set({
              criticalError: error.message || 'Failed to fetch project sections',
              isLoading: false
            });
            // Don't throw error - return empty array
            return [];
          }
        },

        // Create a new section
        createSection: async (projectId, sectionData) => {
          // Store previous state for potential rollback
          const previousProjectSections = { ...get().projectSections };

          // Optimistically update UI - add new section to the project's sections
          const tempId = `temp-${Date.now()}`;
          const newSection = {
            _id: tempId,
            name: sectionData.name,
            projectId,
            orderIndex: sectionData.orderIndex || 0,
            collapsed: false,
            ...sectionData,
            isOptimistic: true // Flag to identify optimistically created sections
          };

          set(state => {
            // Get current sections for this project
            const currentSections = state.projectSections[projectId] || [];

            return {
              projectSections: {
                ...state.projectSections,
                [projectId]: [...currentSections, newSection]
              },
              isLoading: true
            };
          });

          try {
            // Make API call to create section
            const response = await sectionService.createSection(projectId, sectionData);
            const createdSection = response.section;

            // Update state with the actual section data from the server
            set(state => {
              const updatedSections = (state.projectSections[projectId] || []).map(section =>
                section._id === tempId ? createdSection : section
              );

              return {
                projectSections: {
                  ...state.projectSections,
                  [projectId]: updatedSections
                },
                isLoading: false
              };
            });

            // Invalidate cache if API service is available
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache(`/projects/${projectId}/sections`);
                api.invalidateCache(`/projects/${projectId}/tasks`);
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return createdSection;
          } catch (error) {
            // Rollback to previous state on error
            set({
              projectSections: previousProjectSections,
              operationError: error.message || 'Failed to create section',
              isLoading: false
            });

            // Return null to indicate failure instead of throwing
            return null;
          }
        },

        // Update a section
        updateSection: async (sectionId, sectionData) => {
          // Store previous state for potential rollback
          const previousProjectSections = { ...get().projectSections };

          // Find the section and its projectId
          let projectId = null;
          let sectionToUpdate = null;

          // Look for the section in all project sections
          Object.entries(get().projectSections).forEach(([pid, sections]) => {
            const foundSection = sections.find(s => s._id === sectionId);
            if (foundSection) {
              projectId = pid;
              sectionToUpdate = foundSection;
            }
          });

          if (!sectionToUpdate || !projectId) {
            set({ operationError: 'Section not found' });
            return null; // Return null instead of throwing
          }

          // Optimistically update UI
          set(state => {
            const updatedSections = state.projectSections[projectId].map(section =>
              section._id === sectionId ? { ...section, ...sectionData } : section
            );

            return {
              projectSections: {
                ...state.projectSections,
                [projectId]: updatedSections
              },
              isLoading: true
            };
          });

          try {
            // Make API call to update section
            const response = await sectionService.updateSection(sectionId, sectionData);

            // Update state with the response
            set({ isLoading: false });

            // Invalidate cache if API service is available
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache(`/projects/${projectId}/sections`);
                api.invalidateCache(`/sections/${sectionId}`);
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return response.section;
          } catch (error) {
            // Rollback to previous state on error
            set({
              projectSections: previousProjectSections,
              operationError: error.message || 'Failed to update section',
              isLoading: false
            });

            // Return null to indicate failure instead of throwing
            return null;
          }
        },

        // Delete a section
        deleteSection: async (sectionId) => {
          // Store previous state for potential rollback
          const previousProjectSections = { ...get().projectSections };
          const previousProjectTasks = { ...get().projectTasks };

          // Find the section and its projectId
          let projectId = null;
          let sectionToDelete = null;

          // Look for the section in all project sections
          Object.entries(get().projectSections).forEach(([pid, sections]) => {
            const foundSection = sections.find(s => s._id === sectionId);
            if (foundSection) {
              projectId = pid;
              sectionToDelete = foundSection;
            }
          });

          if (!sectionToDelete || !projectId) {
            set({ operationError: 'Section not found' });
            return false; // Return false instead of throwing
          }

          // Optimistically update UI - remove section
          set(state => {
            const updatedSections = state.projectSections[projectId].filter(
              section => section._id !== sectionId
            );

            // Also update tasks that belong to this section - move them to "no section"
            let updatedTasks = state.projectTasks[projectId] || [];
            if (updatedTasks.length > 0) {
              updatedTasks = updatedTasks.map(task =>
                task.sectionId === sectionId ? { ...task, sectionId: null } : task
              );
            }

            return {
              projectSections: {
                ...state.projectSections,
                [projectId]: updatedSections
              },
              projectTasks: {
                ...state.projectTasks,
                [projectId]: updatedTasks
              },
              isLoading: true
            };
          });

          try {
            // Make API call to delete section
            await sectionService.deleteSection(sectionId);

            // Update state
            set({ isLoading: false });

            // Invalidate cache if API service is available
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache(`/projects/${projectId}/sections`);
                api.invalidateCache(`/projects/${projectId}/tasks`);
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return true;
          } catch (error) {
            // Rollback to previous state on error
            set({
              projectSections: previousProjectSections,
              projectTasks: previousProjectTasks,
              error: error.message || 'Failed to delete section',
              isLoading: false
            });

            // Return false to indicate failure instead of throwing
            return false;
          }
        },

        // Reorder sections within a project
        reorderSections: async (projectId, orderedSectionIds) => {
          // Store previous state for potential rollback
          const previousProjectSections = { ...get().projectSections };

          // Verify we have these sections
          const currentSections = get().projectSections[projectId] || [];
          if (currentSections.length === 0 ||
            orderedSectionIds.length !== currentSections.length) {
            set({ error: 'Invalid section order' });
            return null; // Return null instead of throwing
          }

          // Optimistically update UI - reorder sections
          set(state => {
            // Create a map of id -> section for easy lookup
            const sectionsMap = {};
            state.projectSections[projectId].forEach(section => {
              sectionsMap[section._id] = section;
            });

            // Create reordered array with updated orderIndex values
            const reorderedSections = orderedSectionIds.map((id, index) => ({
              ...sectionsMap[id],
              orderIndex: index
            }));

            return {
              projectSections: {
                ...state.projectSections,
                [projectId]: reorderedSections
              },
              isLoading: true
            };
          });

          try {
            // Make API call to reorder sections
            const response = await sectionService.reorderSections(projectId, orderedSectionIds);

            // Update state
            set({ isLoading: false });

            // Invalidate cache if API service is available
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache(`/projects/${projectId}/sections`);
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return response.sections;
          } catch (error) {
            // Rollback to previous state on error
            set({
              projectSections: previousProjectSections,
              error: error.message || 'Failed to reorder sections',
              isLoading: false
            });

            // Return null to indicate failure instead of throwing
            return null;
          }
        },

        // TASK-RELATED METHODS

        // Fetch all tasks
        fetchTasks: async (params = {}) => {
          set({ isLoading: true, criticalError: null });
          try {
            const response = await taskService.getTasks(params);
            const tasks = response.data.tasks;
            
            set(state => {
              // Check if currentTask needs updating
              let updatedCurrentTask = state.currentTask;
              if (state.currentTask && state.currentTask._id) {
                const updatedTask = tasks.find(t => t._id === state.currentTask._id);
                if (updatedTask) {
                  console.log('🚀 ~ fetchTasks: updating currentTask with fresh data');
                  updatedCurrentTask = updatedTask;
                }
              }
              
              return {
                tasks: tasks,
                totalTasks: response.meta.total,
                pagination: {
                  page: response.meta.page || 1,
                  totalPages: response.meta.totalPages || 1,
                  limit: response.meta.limit || 10
                },
                currentTask: updatedCurrentTask,
                isLoading: false
              };
            });
            return tasks;
          } catch (error) {
            set({
              criticalError: error.message || 'Failed to fetch tasks',
              isLoading: false
            });

            // Return empty array instead of throwing
            return [];
          }
        },

// Sync grid tasks from project tasks when a task is updated/created
syncGridTasks: (projectId) => {
  const state = get();
  if (state.gridTasks[projectId] && state.projectTasks[projectId]) {
    // Update grid tasks with any new/updated tasks from project tasks
    const gridTaskIds = new Set(state.gridTasks[projectId].map(t => t._id));
    const newProjectTasks = state.projectTasks[projectId].filter(t => !gridTaskIds.has(t._id));
    
    if (newProjectTasks.length > 0) {
      set(prevState => ({
        gridTasks: {
          ...prevState.gridTasks,
          [projectId]: [...newProjectTasks, ...prevState.gridTasks[projectId]]
        }
      }));
    }
  }
},

// UPDATED METHOD - fetchProjectTasks
fetchProjectTasks: async (projectId, params = {}) => {
  console.log("🚀 ~ fetchProjectTasks: ~ params:", params)
  set({ isLoading: true, criticalError: null });

  try {
    // Check if we want to bypass section-based fetching for higher limits (grid view)
    if (params.limit && params.limit > TASKS_PER_SECTION * 10) {
      // Direct API call for high limits - store in separate gridTasks
      const response = await taskService.getProjectTasks(projectId, params);
      
      set(state => ({
        gridTasks: {
          ...state.gridTasks,
          [projectId]: response.data.tasks
        },
        isLoading: false
      }));
      
      return response.data.tasks;
    }

    // 1. First, fetch sections for this project (original logic for lower limits)
    const sectionsResponse = await sectionService.getProjectSections(projectId);
    const sections = sectionsResponse.sections || [];
    
    // 2. Initialize arrays to collect tasks and pagination data
    const allTasks = [];
    const paginationData = {};
    
    // 3. Fetch tasks for each section
    for (const section of sections) {
      const sectionParams = {
        ...params,
        page: 1,
        sectionId: section._id,
        limit: TASKS_PER_SECTION
      };
      
      const sectionResponse = await taskService.getProjectTasks(projectId, sectionParams);
      
      allTasks.push(...sectionResponse.data.tasks);
      
      paginationData[section._id] = {
        page: sectionParams.page || 1,
        hasMore: sectionResponse.data.tasks.length === TASKS_PER_SECTION && 
                 sectionResponse.data.tasks.length < sectionResponse.meta.total,
        isLoading: false,
        total: sectionResponse.meta.total,
        tasks: sectionResponse.data.tasks.length
      };
      console.log("🚀 ~ fetchProjectTasks: ~ paginationData:", paginationData)

    }
    
    // 4. Fetch unsectioned tasks
    const unsectionedParams = {
      ...params,
      page:1,
      sectionId: null,
      limit: TASKS_PER_SECTION
    };
    
    const unsectionedResponse = await taskService.getProjectTasks(projectId, unsectionedParams);
    allTasks.push(...unsectionedResponse.data.tasks);
    
    paginationData[null] = {
      page: unsectionedParams.page || 1,
      hasMore: unsectionedResponse.data.tasks.length === TASKS_PER_SECTION &&
               unsectionedResponse.data.tasks.length < unsectionedResponse.meta.total,
      isLoading: false,
      total: unsectionedResponse.meta.total,
      tasks: unsectionedResponse.data.tasks.length
    };
    
    // 5. Update state with both sections and tasks
    set(state => {
      // Check if currentTask needs updating
      let updatedCurrentTask = state.currentTask;
      if (state.currentTask && state.currentTask._id) {
        const updatedTask = allTasks.find(t => t._id === state.currentTask._id);
        if (updatedTask) {
          console.log('🚀 ~ fetchProjectTasks: updating currentTask with fresh data');
          updatedCurrentTask = updatedTask;
        }
      }
      
      return {
        projectSections: {
          ...state.projectSections,
          [projectId]: sections
        },
        projectTasks: {
          ...state.projectTasks,
          [projectId]: allTasks
        },
        sectionPagination: {
          ...state.sectionPagination,
          [projectId]: paginationData
        },
        currentTask: updatedCurrentTask,
        isLoading: false
      };
    });

    return {
      tasks: allTasks,
      sections: sections
    };
  } catch (error) {
    set({
      criticalError: error.message || 'Failed to fetch project data',
      isLoading: false
    });
    
    return { tasks: [], sections: [] };
  }
},
// NEW METHOD - loadMoreTasks
loadMoreTasks: async (projectId, sectionId = null) => {
  const state = get();
  const projectPagination = state.sectionPagination[projectId] || {};
  const sectionPagination = projectPagination[sectionId] || { page: 1, hasMore: false };
  console.log('Current pagination:', sectionPagination); // Debug

  if (!sectionPagination.hasMore || sectionPagination.isLoading) {
    return;
  }

  // Update loading state
  set(state => {
    const currentPagination = { ...state.sectionPagination };
    if (!currentPagination[projectId]) {
      currentPagination[projectId] = {};
    }
    currentPagination[projectId][sectionId] = {
      ...sectionPagination,
      isLoading: true
    };
    
    return {
      sectionPagination: currentPagination
    };
  });

  try {
    const nextPage = sectionPagination.page + 1;
    console.log("🚀 ~ loadMoreTasks: ~ nextPage:", nextPage)
    
    // Use optimized endpoints based on whether it's a section or unsectioned tasks
    let tasksResponse;
    if (sectionId === null) {
      // Load more unsectioned tasks
      tasksResponse = await sectionService.loadMoreUnsectionedTasks(projectId, {
        page: nextPage,
        limit: TASKS_PER_SECTION,
        showCompleted: 'true' // TODO: Get this from UI state
      });
    } else {
      // Load more tasks for a specific section
      tasksResponse = await sectionService.loadMoreSectionTasks(sectionId, {
        page: nextPage,
        limit: TASKS_PER_SECTION,
        showCompleted: 'true' // TODO: Get this from UI state
      });
    }

    // Check response structure and extract data
    const responseData = tasksResponse.data || tasksResponse;
    const newTasks = responseData.tasks || [];
    const hasMore = responseData.hasMore;
    const total = responseData.total;

    // Update state with new tasks
    set(state => {
      const currentTasks = state.projectTasks[projectId] || [];
      const updatedTasks = [...currentTasks, ...newTasks];
      
      const currentPagination = { ...state.sectionPagination };
      currentPagination[projectId][sectionId] = {
        page: nextPage,
        hasMore: hasMore,
        isLoading: false,
        total: total,
        tasks: (state.projectTasks[projectId] || []).filter(task => 
          sectionId === null ? !task.sectionId : task.sectionId === sectionId
        ).length + newTasks.length
      };

      return {
        projectTasks: {
          ...state.projectTasks,
          [projectId]: updatedTasks
        },
        sectionPagination: currentPagination
      };
    });

    return newTasks;
  } catch (error) {
    console.log('Failed to load more tasks:', error);
    
    // Reset loading state on error
    set(state => {
      const currentPagination = { ...state.sectionPagination };
      currentPagination[projectId][sectionId] = {
        ...sectionPagination,
        isLoading: false
      };
      
      return {
        sectionPagination: currentPagination,
        criticalError: error.message || 'Failed to load more tasks'
      };
    });
  }
},

// NEW METHOD - resetSectionPagination
resetSectionPagination: (projectId) => {
  set(state => ({
    sectionPagination: {
      ...state.sectionPagination,
      [projectId]: {}
    }
  }));
},

        // Fetch sections with tasks (optimized for Kanban/List views)
        fetchProjectSectionsWithTasks: async (projectId, params = {}) => {
          console.log('🚀 ~ fetchProjectSectionsWithTasks:', { projectId, params });
          set({ isLoading: true, criticalError: null });
          
          try {
            // Set default limit to TASKS_PER_SECTION if not provided
            const fetchParams = {
              limit: TASKS_PER_SECTION,
              ...params
            };
            const response = await sectionService.getProjectSectionsWithTasks(projectId, fetchParams);
            console.log('🚀 ~ fetchProjectSectionsWithTasks response:', response);
            
            // The response should have status and data properties
            if (!response || !response.data || response.status !== 'success') {
              console.error('Invalid response structure:', response);
              throw new Error('Invalid response from server');
            }
            
            const { sections, unsectionedTasks } = response.data;
            
            // Build projectTasks array from sections data
            const allTasks = [];
            const paginationData = {};
            
            // Add tasks from sections
            sections.forEach(section => {
              allTasks.push(...section.tasks);
              paginationData[section._id] = {
                page: 1,
                hasMore: section.hasMore,
                total: section.taskCount,
                isLoading: false
              };
            });
            
            // Add unsectioned tasks
            allTasks.push(...unsectionedTasks.tasks);
            paginationData[null] = {
              page: 1,
              hasMore: unsectionedTasks.hasMore,
              total: unsectionedTasks.taskCount,
              isLoading: false
            };
            
            set(state => ({
              projectSections: {
                ...state.projectSections,
                [projectId]: sections.map(s => ({
                  _id: s._id,
                  name: s.name,
                  color: s.color,
                  orderIndex: s.orderIndex,
                  collapsed: s.collapsed,
                  projectId: s.projectId
                }))
              },
              projectTasks: {
                ...state.projectTasks,
                [projectId]: allTasks
              },
              sectionPagination: {
                ...state.sectionPagination,
                [projectId]: paginationData
              },
              isLoading: false
            }));
            
            return {
              tasks: allTasks,
              sections: sections
            };
          } catch (error) {
            set({
              criticalError: error.message || 'Failed to fetch sections with tasks',
              isLoading: false
            });
            return { tasks: [], sections: [] };
          }
        },

        // Fetch tasks for calendar view with date range
        fetchCalendarTasks: async (projectId, dateRange) => {
          console.log('🚀 ~ fetchCalendarTasks:', { projectId, dateRange });
          set({ isLoading: true, criticalError: null });
          
          const { startDate, endDate } = dateRange;
          const cacheKey = `${projectId}-${startDate}-${endDate}`;
          
          // Always fetch fresh data for calendar view
          try {
            const response = await taskService.getProjectTasks(projectId, {
              dueAfter: startDate,
              dueBefore: endDate,
              limit: 500 // Get all tasks for the date range
            });
            
            const tasks = response.data.tasks || [];
            
            console.log(`🚀 ~ fetchCalendarTasks: fetched ${tasks.length} tasks for date range`);
            
            set(state => ({
              calendarTasks: {
                ...state.calendarTasks,
                [cacheKey]: tasks
              },
              calendarCache: {
                ...state.calendarCache,
                [cacheKey]: {
                  timestamp: Date.now(),
                  count: tasks.length
                }
              },
              isLoading: false
            }));
            
            return tasks;
          } catch (error) {
            set({
              criticalError: error.message || 'Failed to fetch calendar tasks',
              isLoading: false
            });
            return [];
          }
        },
        
        // Clear calendar cache for a project
        clearCalendarCache: (projectId) => {
          set(state => {
            const newCalendarTasks = { ...state.calendarTasks };
            const newCalendarCache = { ...state.calendarCache };
            
            // Remove all entries for this project
            Object.keys(newCalendarTasks).forEach(key => {
              if (key.startsWith(projectId)) {
                delete newCalendarTasks[key];
                delete newCalendarCache[key];
              }
            });
            
            return {
              calendarTasks: newCalendarTasks,
              calendarCache: newCalendarCache
            };
          });
        },

        // Fetch tasks assigned to current user
        fetchMyTasks: async (params = {}) => {
          set({ isLoading: true, criticalError: null });
          try {
            const response = await taskService.getMyTasks(params);
            const tasks = response.data.tasks;
            
            set(state => {
              // Check if currentTask needs updating
              let updatedCurrentTask = state.currentTask;
              if (state.currentTask && state.currentTask._id) {
                const updatedTask = tasks.find(t => t._id === state.currentTask._id);
                if (updatedTask) {
                  console.log('🚀 ~ fetchMyTasks: updating currentTask with fresh data');
                  updatedCurrentTask = updatedTask;
                }
              }
              
              return {
                myTasks: tasks,
                totalTasks: response.meta.total,
                pagination: {
                  page: response.meta.page || 1,
                  totalPages: response.meta.totalPages || 1,
                  limit: response.meta.limit || 10
                },
                currentTask: updatedCurrentTask,
                isLoading: false
              };
            });
            return tasks;
          } catch (error) {
            set({
              criticalError: error.message || 'Failed to fetch your tasks',
              isLoading: false
            });

            // Return empty array instead of throwing
            return [];
          }
        },

        // Fetch single task
        fetchTask: async (taskId) => {
          set({ isLoading: true, criticalError: null });

          // Check if we already have this task in our store
          const existingTask = get().currentTask?._id === taskId ? get().currentTask : null;
          if (existingTask) {
            set({ isLoading: false });
            return existingTask;
          }

          try {
            const response = await taskService.getTask(taskId);
            console.log("🚀 ~ fetchTask: ~ response:", response)
            const task = response.data?.task || response.task || response;
            console.log("🚀 ~ fetchTask: ~ extracted task:", task);
            console.log("🚀 ~ fetchTask: ~ setting currentTask in store");
            set({ currentTask: task, isLoading: false });
            
            // Verify the update
            const newState = get();
            console.log("🚀 ~ fetchTask: ~ currentTask after set:", newState.currentTask);
            
            return task;
          } catch (error) {
            set({
              criticalError: error.message || 'Failed to fetch task details',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Create task with section support
        createTask: async (taskData, callback = null) => {
          set({ isLoading: true, criticalError: null });
          try {
            // Extract section ID if present (API expects sectionId as a parameter)
            const sectionId = taskData.sectionId;
            
            // Extract grid assignment metadata and remove from API data
            const gridAssignment = taskData._gridAssignment;
            const apiTaskData = { ...taskData };
            delete apiTaskData._gridAssignment;
            
            console.log('Creating task with grid assignment:', { 
              hasGridAssignment: !!gridAssignment, 
              gridAssignment,
              taskTitle: apiTaskData.title 
            });

            // Make API call with section ID as a parameter if present
            const response = await taskService.createTask(apiTaskData);
            const newTask = response.data.task;

            // Optimistically update state with duplicate prevention
            set(state => {
              // Check if task already exists to prevent duplicates
              const taskExists = state.tasks.some(t => t._id === newTask._id);
              if (taskExists) {
                return { isLoading: false };
              }

              // Update tasks list
              const updatedTasks = [newTask, ...state.tasks];

              // Update project tasks if this task belongs to a project
              const projectId = taskData.projectId;
              let updatedProjectTasks = { ...state.projectTasks };

              if (projectId) {
                const projectTasks = state.projectTasks[projectId] || [];
                // Check for duplicate in project tasks
                if (!projectTasks.some(t => t._id === newTask._id)) {
                  updatedProjectTasks[projectId] = [newTask, ...projectTasks];
                }
              }

              // Update my tasks if this task is assigned to the current user
              let updatedMyTasks = [...state.myTasks];
              if (taskData.assignedTo && taskData.assignedTo === state.userId) {
                // Check for duplicate in my tasks
                if (!state.myTasks.some(t => t._id === newTask._id)) {
                  updatedMyTasks = [newTask, ...state.myTasks];
                }
              }

              return {
                tasks: updatedTasks,
                projectTasks: updatedProjectTasks,
                myTasks: updatedMyTasks,
                currentTask: newTask, // Set the newly created task as currentTask with full content
                isLoading: false
              };
            });

            // Invalidate cache for related endpoints if API cache function exists
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache('/tasks');
                if (taskData.projectId) {
                  api.invalidateCache(`/projects/${taskData.projectId}/tasks`);
                }
                if (sectionId) {
                  api.invalidateCache(`/sections/${sectionId}/tasks`);
                }
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            // Handle grid assignment if metadata is present
            if (gridAssignment && gridAssignment.autoAssign) {
              console.log('Auto-assigning task to grid:', { taskId: newTask._id, gridAssignment });
              
              // Import grid service and assign task to grid
              try {
                const gridService = await import('../services/gridService');
                const assignResponse = await gridService.default.assignTask(
                  gridAssignment.gridId, 
                  newTask._id, 
                  gridAssignment.row, 
                  gridAssignment.column
                );
                console.log('Task successfully auto-assigned to grid:', assignResponse);
                
                // Update the newly created task with grid assignment info
                newTask.gridAssignments = [{
                  gridTemplateId: gridAssignment.gridId,
                  cellPosition: {
                    row: gridAssignment.row,
                    column: gridAssignment.column
                  }
                }];
                
              } catch (gridError) {
                console.error('Failed to auto-assign task to grid:', gridError);
                console.error('Grid assignment details:', gridAssignment);
              }
            }

            // Sync to grid tasks if grid tasks exist for this project
            if (taskData.projectId) {
              get().syncGridTasks(taskData.projectId);
            }

            // Run callback if provided
            if (callback && typeof callback === 'function') {
              callback(newTask);
            }

            return newTask;
          } catch (error) {
            set({
              error: error.message || 'Failed to create task',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Update task with section support (legacy)
        updateTask: async (taskId, taskData) => {
          console.log("🚀 ~ updateTask: ~ taskData:", taskData)
          console.log("🚀 ~ updateTask: ~ taskId:", taskId)
          // Store previous state for rollback
          const previousState = {
            tasks: [...get()?.tasks],
            projectTasks: { ...get()?.projectTasks },
            myTasks: [...get()?.myTasks],
            currentTask: get().currentTask ? { ...get().currentTask } : null,
            sections: { ...get()?.sections }
          };

          // Find the task in our state
          const task = get()?.currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            set({ error: 'Task not found in state' });
            return null; // Return null instead of throwing
          }

          // Determine which collections this task belongs to
          const projectId = task.projectId?._id || task.projectId;
          const isInProjectTasks = projectId && get().projectTasks[projectId]?.some(t => t._id === taskId);
          const isInMyTasks = get().myTasks.some(t => t._id === taskId);

          // Handle section updates
          const oldSectionId = task.sectionId;
          const newSectionId = taskData.sectionId;

          // Create updated task for optimistic update
          const updatedTask = { ...task, ...taskData };

          // Optimistically update UI
          set(state => {
            // Update tasks array
            const newTasks = state.tasks.map(t =>
              t._id === taskId ? updatedTask : t
            );

            // Update my tasks array
            const newMyTasks = isInMyTasks
              ? state.myTasks.map(t => t._id === taskId ? updatedTask : t)
              : state.myTasks;

            // Update project tasks
            let newProjectTasks = { ...state.projectTasks };
            if (isInProjectTasks) {
              newProjectTasks[projectId] = state.projectTasks[projectId].map(t =>
                t._id === taskId ? updatedTask : t
              );
            }

            // Update current task
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              tasks: newTasks,
              myTasks: newMyTasks,
              projectTasks: newProjectTasks,
              currentTask: newCurrentTask,
              isLoading: true,
              error: null
            };
          });

          try {
            // Extract section ID if present (API expects sectionId as a parameter)
            // const sectionId = taskData.sectionId;
            const apiTaskData = { ...taskData };
            // console.log("API apiTaskData:", apiTaskData);

            // Make API call with section ID as a parameter if it changed
            let response;
            if (oldSectionId !== newSectionId && newSectionId !== undefined) {
              // console.log("🚀 ~ updateTask: ~ newSectionId:", newSectionId)
              // console.log("🚀 ~ updateTask: ~ oldSectionId:", oldSectionId)
              response = await taskService.updateTaskSection(taskId, apiTaskData, newSectionId);
            } else {
              response = await taskService.updateTask(taskId, apiTaskData);
            }

            // Log the response structure to understand its format
            console.log("API Response:", response);

            // Get the server-updated task - handle different possible response structures
            let serverUpdatedTask;

            if (response.data && response.data.task) {
              // Structure: response.data.task
              serverUpdatedTask = response.data.task;
            } else if (response.task) {
              // Structure: response.task
              serverUpdatedTask = response.task;
            } else if (response.data) {
              // Structure: response.data (when data is the task itself)
              serverUpdatedTask = response.data;
            } else {
              // Fallback: use our optimistic update as best guess
              console.warn("Unexpected API response structure:", response);
              serverUpdatedTask = updatedTask;
            }

            // Update state with the actual server response data
            set(state => {
              // Update tasks array with server data
              const newTasks = state.tasks.map(t =>
                t._id === taskId ? serverUpdatedTask : t
              );

              // Update my tasks array with server data
              const newMyTasks = isInMyTasks
                ? state.myTasks.map(t => t._id === taskId ? serverUpdatedTask : t)
                : state.myTasks;

              // Update project tasks with server data
              let newProjectTasks = { ...state.projectTasks };
              if (isInProjectTasks) {
                newProjectTasks[projectId] = state.projectTasks[projectId].map(t =>
                  t._id === taskId ? serverUpdatedTask : t
                );
              }

              // Update current task with server data
              const newCurrentTask = state.currentTask?._id === taskId
                ? serverUpdatedTask
                : state.currentTask;

              return {
                tasks: newTasks,
                myTasks: newMyTasks,
                projectTasks: newProjectTasks,
                currentTask: newCurrentTask,
                isLoading: false
              };
            });

            // Invalidate cache for related endpoints if API cache function exists
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache('/tasks');
                api.invalidateCache(`/tasks/${taskId}`);
                if (projectId) {
                  api.invalidateCache(`/projects/${projectId}/tasks`);
                }
                if (oldSectionId) {
                  api.invalidateCache(`/sections/${oldSectionId}/tasks`);
                }
                if (newSectionId && newSectionId !== oldSectionId) {
                  api.invalidateCache(`/sections/${newSectionId}/tasks`);
                }
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return serverUpdatedTask;
          } catch (error) {
            console.log("Error updating task:", error);
            // Rollback to previous state on error
            set({
              tasks: previousState.tasks,
              myTasks: previousState.myTasks,
              projectTasks: previousState.projectTasks,
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to update task',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Unified PATCH update method - handles all types of updates
        patchTask: async (taskId, updates) => {
          console.log("🚀 ~ patchTask: ~ updates:", updates);
          console.log("🚀 ~ patchTask: ~ taskId:", taskId);
          
          // Add detailed debug logging for checklist operations
          if (updates.checklist) {
            console.log("🚀 ~ patchTask: ~ CHECKLIST OPERATION DETECTED", updates.checklist);
          }
          
          // Store previous state for rollback
          const previousState = {
            tasks: [...get()?.tasks],
            projectTasks: { ...get()?.projectTasks },
            myTasks: [...get()?.myTasks],
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task in our state
          let task = get()?.currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId) || 
              get().myTasks.find(t => t._id === taskId) ||
              Object.values(get().projectTasks).flat().find(t => t._id === taskId);

          if (!task) {
            // Try to fetch the task if not in state
            try {
              const fetchedTask = await taskService.getTask(taskId);
              if (!fetchedTask?.data?.task) {
                set({ error: 'Task not found' });
                return null;
              }
              // Continue with the fetched task
              task = fetchedTask.data.task;
              set({ currentTask: task });
            } catch (error) {
              set({ error: 'Task not found' });
              return null;
            }
          }

          // Determine which collections this task belongs to
          const projectId = task.projectId?._id || task.projectId;
          const isInProjectTasks = projectId && get().projectTasks[projectId]?.some(t => t._id === taskId);
          const isInMyTasks = get().myTasks.some(t => t._id === taskId);

          // Create optimistic updates only for basic fields
          const optimisticUpdates = {};
          const basicFields = ['title', 'description', 'priority', 'dueDate', 'status', 
                              'assignedTo', 'sectionId', 'estimatedHours', 'actualHours', 
                              'type', 'contentStage', 'isClientVisible', 'tags', 'orderIndex'];
          
          basicFields.forEach(field => {
            if (updates[field] !== undefined) {
              optimisticUpdates[field] = updates[field];
            }
          });

          // Create updated task for optimistic update (only basic fields)
          const optimisticTask = { ...task, ...optimisticUpdates };

          // Optimistically update UI for basic fields only
          if (Object.keys(optimisticUpdates).length > 0) {
            set(state => {
              // Update tasks array WITHOUT re-sorting (to maintain order in all tasks view)
              const updatedTasks = state.tasks.map(t =>
                t._id === taskId ? optimisticTask : t
              );
              const newTasks = updatedTasks; // Don't sort - maintain existing order

              // Update my tasks array WITHOUT re-sorting (to maintain order in my tasks view)
              const updatedMyTasks = isInMyTasks
                ? state.myTasks.map(t => t._id === taskId ? optimisticTask : t)
                : state.myTasks;
              const newMyTasks = updatedMyTasks; // Don't sort - maintain existing order

              // Update project tasks and maintain ordering
              let newProjectTasks = { ...state.projectTasks };
              if (isInProjectTasks) {
                const updatedProjectTasks = state.projectTasks[projectId].map(t =>
                  t._id === taskId ? optimisticTask : t
                );
                newProjectTasks[projectId] = sortTasksByOrder(updatedProjectTasks);
              }

              // Update current task
              const newCurrentTask = state.currentTask?._id === taskId
                ? optimisticTask
                : state.currentTask;

              return {
                tasks: newTasks,
                myTasks: newMyTasks,
                projectTasks: newProjectTasks,
                currentTask: newCurrentTask,
                isLoading: true,
                error: null
              };
            });
          } else {
            // No optimistic updates, just set loading
            set({ isLoading: true, criticalError: null });
          }

          try {
            // Make API call using new PATCH endpoint
            console.log("🚀 ~ patchTask: ~ CALLING API with updates:", updates);
            const response = await taskService.patchTask(taskId, updates);
            
            console.log("🚀 ~ patchTask: ~ PATCH API Response:", response);
            console.log("🚀 ~ patchTask: ~ Response data:", response.data);
            console.log("🚀 ~ patchTask: ~ Response task:", response.data?.task);
            console.log("🚀 ~ patchTask: ~ Response task checklist:", response.data?.task?.checklist);

            // Get the server-updated task
            const serverUpdatedTask = response.data?.task || response.task || response;
            console.log("🚀 ~ patchTask: ~ serverUpdatedTask:", serverUpdatedTask);
            console.log("🚀 ~ patchTask: ~ serverUpdatedTask checklist:", serverUpdatedTask?.checklist);

            // Update state with the actual server response data
            console.log("🚀 ~ patchTask: ~ UPDATING STATE with serverUpdatedTask");
            set(state => {
              console.log("🚀 ~ patchTask: ~ STATE UPDATE - Previous currentTask:", state.currentTask);
              console.log("🚀 ~ patchTask: ~ STATE UPDATE - Previous currentTask checklist:", state.currentTask?.checklist);
              
              // Update tasks array with server data WITHOUT re-sorting (to maintain order in all tasks view)
              const updatedTasks = state.tasks.map(t =>
                t._id === taskId ? serverUpdatedTask : t
              );
              const newTasks = updatedTasks; // Don't sort - maintain existing order

              // Update my tasks array with server data WITHOUT re-sorting (to maintain order in my tasks view)  
              const updatedMyTasks = isInMyTasks
                ? state.myTasks.map(t => t._id === taskId ? serverUpdatedTask : t)
                : state.myTasks;
              const newMyTasks = updatedMyTasks; // Don't sort - maintain existing order

              // Update project tasks with server data and maintain ordering
              let newProjectTasks = { ...state.projectTasks };
              if (isInProjectTasks) {
                const updatedProjectTasks = state.projectTasks[projectId].map(t =>
                  t._id === taskId ? serverUpdatedTask : t
                );
                newProjectTasks[projectId] = sortTasksByOrder(updatedProjectTasks);
              }
              
              // Debug ordering in development after task updates
              if (process.env.NODE_ENV === 'development') {
                debugTaskOrdering(newTasks, 'TaskStore - Updated tasks array');
                if (isInMyTasks) {
                  debugTaskOrdering(newMyTasks, 'TaskStore - Updated myTasks array');
                }
                if (isInProjectTasks) {
                  debugTaskOrdering(newProjectTasks[projectId], `TaskStore - Updated projectTasks[${projectId}]`);
                }
              }

              // Update current task with server data
              const newCurrentTask = state.currentTask?._id === taskId
                ? serverUpdatedTask
                : state.currentTask;

              console.log("🚀 ~ patchTask: ~ STATE UPDATE - New currentTask:", newCurrentTask);
              console.log("🚀 ~ patchTask: ~ STATE UPDATE - New currentTask checklist:", newCurrentTask?.checklist);

              return {
                tasks: newTasks,
                myTasks: newMyTasks,
                projectTasks: newProjectTasks,
                currentTask: newCurrentTask,
                isLoading: false
              };
            });

            // Invalidate cache for related endpoints
            try {
              if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
                api.invalidateCache('/tasks');
                api.invalidateCache(`/tasks/${taskId}`);
                if (projectId) {
                  api.invalidateCache(`/projects/${projectId}/tasks`);
                }
                
                // Handle section-specific cache invalidation
                if (updates.sectionId !== undefined) {
                  const oldSectionId = task.sectionId;
                  const newSectionId = updates.sectionId;
                  
                  if (oldSectionId) {
                    api.invalidateCache(`/sections/${oldSectionId}/tasks`);
                  }
                  if (newSectionId && newSectionId !== oldSectionId) {
                    api.invalidateCache(`/sections/${newSectionId}/tasks`);
                  }
                }
              }
            } catch (cacheError) {
              console.warn('Cache invalidation failed:', cacheError);
            }

            return {
              task: serverUpdatedTask,
              changes: response.data?.changes,
              activities: response.data?.activities
            };
          } catch (error) {
            console.error("Error patching task:", error);
            // Rollback to previous state on error
            set({
              tasks: previousState.tasks,
              myTasks: previousState.myTasks,
              projectTasks: previousState.projectTasks,
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to update task',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Reorder tasks within a section - WITH OPTIMISTIC UPDATES
        reorderSectionTasks: async (sectionId, taskIds) => {
          console.log('[taskStore] reorderSectionTasks called:', { sectionId, taskIds });
          
          const state = get();
          
          // First, perform optimistic update for immediate UI feedback
          const operationId = state.optimisticReorderTasks(sectionId, taskIds);
          
          if (!operationId) {
            console.error('[taskStore] Failed to perform optimistic reorder');
            return null;
          }

          try {
            // Make API call to reorder tasks
            const response = await taskService.reorderSectionTasks(sectionId, taskIds);

            // Clear the pending operation on success
            state.clearPendingOperation(operationId);
            
            console.log('[taskStore] Reorder confirmed by server');

            return response.tasks;
          } catch (error) {
            console.error('[taskStore] Error reordering tasks:', error);
            
            // Rollback the optimistic update on error
            state.rollbackOptimisticUpdate(operationId);
            
            set({
              operationError: error.message || 'Failed to reorder tasks'
            });

            return null;
          }
        },

        // ========================================
        // OPTIMISTIC UPDATE METHODS
        // ========================================
        
        // Helper to generate operation ID
        generateOperationId: () => `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        
        // Track a pending operation
        trackPendingOperation: (operationId, type, data) => {
          const state = get();
          const pendingOps = new Map(state.pendingOperations);
          pendingOps.set(operationId, {
            type,
            timestamp: Date.now(),
            data
          });
          set({ pendingOperations: pendingOps });
        },
        
        // Mark an operation as pending
        markOperationPending: (type, entityId, data = {}) => {
          const operationId = `${type}-${entityId}-${Date.now()}`;
          const pendingOps = new Map(get().pendingOperations);
          pendingOps.set(operationId, {
            type,
            timestamp: Date.now(),
            data: { entityId, ...data }
          });
          set({ pendingOperations: pendingOps });
          return operationId;
        },
        
        // Clear a pending operation
        clearPendingOperation: (operationId) => {
          const state = get();
          const pendingOps = new Map(state.pendingOperations);
          pendingOps.delete(operationId);
          set({ pendingOperations: pendingOps });
        },
        
        // Check if an operation is pending for a specific entity
        isPendingOperation: (entityId, type) => {
          const state = get();
          for (const [opId, op] of state.pendingOperations) {
            if (op.type === type && op.data.entityId === entityId) {
              // Check if operation is still fresh (use custom timeout or default to 5 seconds)
              const timeout = op.data.timeout || 5000;
              if (Date.now() - op.timestamp < timeout) {
                return true;
              }
            }
          }
          return false;
        },
        
        // Optimistically move task (immediate UI update)
        optimisticMoveTask: (taskId, targetSectionId, position) => {
          console.log('[taskStore] optimisticMoveTask:', { taskId, targetSectionId, position });
          
          const state = get();
          let originalTask = null;
          let projectId = null;
          let sourceSectionId = null;
          
          // Find the task and its project
          for (const pid in state.projectTasks) {
            const task = state.projectTasks[pid].find(t => t._id === taskId);
            if (task) {
              originalTask = { ...task };
              projectId = pid;
              sourceSectionId = task.sectionId || null;
              break;
            }
          }
          
          if (!originalTask || !projectId) {
            console.error('[taskStore] Task not found for optimistic update');
            return null;
          }
          
          // Generate operation ID for tracking
          const operationId = state.generateOperationId();
          
          // Track this operation as pending
          state.trackPendingOperation(operationId, 'task:move', {
            entityId: taskId,
            taskId,
            sourceSectionId,
            targetSectionId,
            position,
            projectId,
            originalTask
          });
          
          // Perform optimistic update
          const projectTasks = { ...state.projectTasks };
          const tasks = [...projectTasks[projectId]];
          
          // Update task's section
          const taskIndex = tasks.findIndex(t => t._id === taskId);
          if (taskIndex !== -1) {
            tasks[taskIndex] = {
              ...tasks[taskIndex],
              sectionId: targetSectionId,
              orderIndex: position
            };
          }
          
          // Get all tasks in the target section and reorder them
          const targetSectionTasks = tasks
            .filter(t => (t.sectionId || null) === (targetSectionId || null))
            .sort((a, b) => {
              if (a._id === taskId) return position - (b.orderIndex || 0);
              if (b._id === taskId) return (a.orderIndex || 0) - position;
              return (a.orderIndex || 0) - (b.orderIndex || 0);
            });
          
          // Update orderIndex for all affected tasks
          targetSectionTasks.forEach((task, index) => {
            const tIndex = tasks.findIndex(t => t._id === task._id);
            if (tIndex !== -1) {
              tasks[tIndex] = {
                ...tasks[tIndex],
                orderIndex: index
              };
            }
          });
          
          // If moving between sections, also reorder source section
          if (sourceSectionId !== targetSectionId) {
            const sourceSectionTasks = tasks
              .filter(t => (t.sectionId || null) === (sourceSectionId || null) && t._id !== taskId)
              .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
            
            sourceSectionTasks.forEach((task, index) => {
              const tIndex = tasks.findIndex(t => t._id === task._id);
              if (tIndex !== -1) {
                tasks[tIndex] = {
                  ...tasks[tIndex],
                  orderIndex: index
                };
              }
            });
          }
          
          projectTasks[projectId] = tasks;
          
          // Update section pagination counts
          const sectionPagination = { ...state.sectionPagination };
          if (sectionPagination[projectId]) {
            const projectPagination = { ...sectionPagination[projectId] };
            
            // Decrease count in source section
            if (sourceSectionId !== null && projectPagination[sourceSectionId]) {
              projectPagination[sourceSectionId] = {
                ...projectPagination[sourceSectionId],
                total: Math.max(0, (projectPagination[sourceSectionId].total || 0) - 1)
              };
            } else if (sourceSectionId === null && projectPagination[null]) {
              // Handle "no section" case
              projectPagination[null] = {
                ...projectPagination[null],
                total: Math.max(0, (projectPagination[null].total || 0) - 1)
              };
            }
            
            // Increase count in target section
            if (targetSectionId !== null && projectPagination[targetSectionId]) {
              projectPagination[targetSectionId] = {
                ...projectPagination[targetSectionId],
                total: (projectPagination[targetSectionId].total || 0) + 1
              };
            } else if (targetSectionId === null && projectPagination[null]) {
              // Handle "no section" case
              projectPagination[null] = {
                ...projectPagination[null],
                total: (projectPagination[null].total || 0) + 1
              };
            }
            
            sectionPagination[projectId] = projectPagination;
          }
          
          // Update state immediately
          set({ 
            projectTasks,
            sectionPagination,
            lastUpdate: Date.now() // Force re-render
          });
          
          return operationId;
        },
        
        // Optimistically reorder tasks within a section
        optimisticReorderTasks: (sectionId, taskIds) => {
          console.log('[taskStore] optimisticReorderTasks:', { sectionId, taskIds });
          
          const state = get();
          let projectId = null;
          
          // Find project containing these tasks
          for (const pid in state.projectTasks) {
            const hasTask = taskIds.some(taskId => 
              state.projectTasks[pid].some(t => t._id === taskId)
            );
            if (hasTask) {
              projectId = pid;
              break;
            }
          }
          
          if (!projectId) {
            console.error('[taskStore] Project not found for tasks');
            return null;
          }
          
          // Generate operation ID
          const operationId = state.generateOperationId();
          
          // Track operation
          state.trackPendingOperation(operationId, 'section:reorder', {
            entityId: sectionId,
            sectionId,
            taskIds,
            projectId
          });
          
          // Perform optimistic update
          const projectTasks = { ...state.projectTasks };
          const tasks = [...projectTasks[projectId]];
          
          // Update orderIndex for reordered tasks
          taskIds.forEach((taskId, index) => {
            const taskIndex = tasks.findIndex(t => t._id === taskId);
            if (taskIndex !== -1) {
              tasks[taskIndex] = {
                ...tasks[taskIndex],
                orderIndex: index
              };
            }
          });
          
          projectTasks[projectId] = tasks;
          
          // Update state
          set({ 
            projectTasks,
            lastUpdate: Date.now()
          });
          
          return operationId;
        },
        
        // Reconcile optimistic update with server response
        reconcileOptimisticUpdate: (operationId, serverData) => {
          const state = get();
          const operation = state.pendingOperations.get(operationId);
          
          if (!operation) {
            console.log('[taskStore] No pending operation found for reconciliation');
            return;
          }
          
          // Clear the pending operation
          state.clearPendingOperation(operationId);
          
          // If server data differs from optimistic update, apply server data
          if (serverData && serverData.tasks) {
            const { projectId } = operation.data;
            if (projectId) {
              const projectTasks = { ...state.projectTasks };
              const tasks = [...projectTasks[projectId]];
              
              // Update tasks with server data
              serverData.tasks.forEach(serverTask => {
                const index = tasks.findIndex(t => t._id === serverTask._id);
                if (index !== -1) {
                  tasks[index] = { ...tasks[index], ...serverTask };
                }
              });
              
              projectTasks[projectId] = tasks;
              set({ projectTasks });
            }
          }
        },
        
        // Rollback optimistic update on error
        rollbackOptimisticUpdate: (operationId) => {
          const state = get();
          const operation = state.pendingOperations.get(operationId);
          
          if (!operation) {
            console.log('[taskStore] No pending operation found for rollback');
            return;
          }
          
          const { type, data } = operation;
          
          if (type === 'task:move' && data.originalTask) {
            // Restore original task state
            const { projectId, originalTask } = data;
            const projectTasks = { ...state.projectTasks };
            const tasks = [...projectTasks[projectId]];
            
            const taskIndex = tasks.findIndex(t => t._id === originalTask._id);
            if (taskIndex !== -1) {
              tasks[taskIndex] = originalTask;
            }
            
            projectTasks[projectId] = tasks;
            set({ projectTasks });
          }
          
          // Clear the pending operation
          state.clearPendingOperation(operationId);
        },

        // Reposition task (move to section and/or reorder) - WITH OPTIMISTIC UPDATES
        repositionTask: async (taskId, targetSectionId, position) => {
          console.log('[taskStore] repositionTask called:', { 
            taskId, 
            targetSectionId, 
            position,
            targetSectionIdType: typeof targetSectionId,
            isNull: targetSectionId === null,
            isUndefined: targetSectionId === undefined
          });
          
          const state = get();
          
          // First, perform optimistic update for immediate UI feedback
          const operationId = state.optimisticMoveTask(taskId, targetSectionId, position);
          
          if (!operationId) {
            console.error('[taskStore] Failed to perform optimistic update');
            return null;
          }
          
          try {
            // Make API call to reposition the task
            console.log('[taskStore] Calling taskService.repositionTask');
            const response = await taskService.repositionTask(taskId, targetSectionId, position);
            console.log('[taskStore] API response:', response);
            
            // Clear the pending operation on success
            state.clearPendingOperation(operationId);
            
            // If response contains updated task data, reconcile with optimistic update
            if (response && response.data) {
              const updatedTask = response.data.task;
              const affectedTasks = response.data.affectedTasks || {};
              
              console.log('[taskStore] Server confirmed update');
              console.log('[taskStore] Updated task:', updatedTask);
              console.log('[taskStore] Affected tasks:', affectedTasks);
              
              // Apply any differences from server response
              if (affectedTasks && Object.keys(affectedTasks).length > 0) {
                // Server provided different ordering, apply it
                state.reconcileOptimisticUpdate(operationId, {
                  tasks: Object.values(affectedTasks)
                });
              }
              
              return response.data;
            }
            
            return response;
          } catch (error) {
            console.error('[taskStore] Error repositioning task:', error);
            
            // Rollback the optimistic update on error
            state.rollbackOptimisticUpdate(operationId);
            
            set({
              operationError: error.message || 'Failed to reposition task'
            });
            return null;
          }
        },

        // Move task to section
        moveTaskToSection: async (taskId, sectionId) => {
          try {
            set({ isLoading: true, criticalError: null });
        
            // Find the task to get its current section
            let currentSectionId = null;
            let projectId = null;
            const allTasks = get().projectTasks;
            
            // Find task and its project/section
            for (const pid in allTasks) {
              const task = allTasks[pid].find(t => t._id === taskId);
              if (task) {
                currentSectionId = task.sectionId;
                projectId = pid;
                break;
              }
            }
        
            // Make API call via your service
            const response = await taskService.moveTaskToSection(taskId, sectionId);
        
            // Get current task data
            const updatedProjectTasks = {};
            let taskFound = false;
        
            for (const pid in allTasks) {
              const tasks = allTasks[pid];
              const updated = tasks.map(task => {
                if (task._id === taskId) {
                  taskFound = true;
                  return { ...task, sectionId };
                }
                return task;
              });
        
              updatedProjectTasks[pid] = updated;
            }
        
            // Update pagination counts if we found the project and sections
            if (projectId && get().sectionPagination[projectId]) {
              const paginationData = { ...get().sectionPagination[projectId] };
              
              // Update source section count (decrement)
              if (currentSectionId !== null && paginationData[currentSectionId]) {
                paginationData[currentSectionId] = {
                  ...paginationData[currentSectionId],
                  total: Math.max(0, (paginationData[currentSectionId].total || 0) - 1)
                };
              }
              
              // Update destination section count (increment)
              if (sectionId !== null && paginationData[sectionId]) {
                paginationData[sectionId] = {
                  ...paginationData[sectionId],
                  total: (paginationData[sectionId].total || 0) + 1
                };
              }
              
              // Update state with new pagination data
              set({
                projectTasks: updatedProjectTasks,
                sectionPagination: {
                  ...get().sectionPagination,
                  [projectId]: paginationData
                },
                isLoading: false
              });
            } else {
              // Just update tasks if pagination data not found
              set({
                projectTasks: updatedProjectTasks,
                isLoading: false
              });
            }
        
            // If task not found, log a warning but don't fail the operation
            if (!taskFound) {
              console.warn(`Task ${taskId} not found in local state, but updated on server`);
            }
        
            return response;
          } catch (error) {
            console.log('Error moving task to section:', error);
            set({
              error: error.message || 'Failed to move task to section',
              isLoading: false
            });
        
            // Return null instead of throwing
            return null;
          }
        },

        // Update task status (with section support)
        updateTaskStatus: async (taskId, newStatus) => {
          // Use the updateTask method with status update
          return get().updateTask(taskId, { status: newStatus });
        },

        // Add a new function in taskStore.js
        addTaskComment: async (taskId, commentText, isInternal = false) => {
          // Store previous state for potential rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Get current user info from auth store
          const user = useAuthStore.getState().user;
          if (!user) {
            const error = new Error('User not authenticated');
            set({ error: error.message });
            throw error;
          }

          // Create new comment for optimistic update
          const newComment = {
            _id: `temp-${Date.now()}`, // Temporary ID for optimistic update
            text: commentText,
            isInternal,
            user: {
              _id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              photo: user.photo
            },
            createdAt: new Date().toISOString()
          };

          // Create updated task with new comment
          const updatedTask = {
            ...task,
            comments: [...(task.comments || []), newComment]
          };

          // Optimistically update UI
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call to add the comment using the dedicated endpoint
            const response = await api.post(`/tasks/${taskId}/comments`, {
              text: commentText,
              isInternal
            });

            // Get the newly created comment from the response
            const commentFromServer = response.data?.comment || response.comment;

            // Fetch the updated task to ensure we have the correct data
            await get().fetchTask(taskId);

            set({ isLoading: false });

            return commentFromServer;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to add comment',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Add checklist item to task
        addTaskChecklistItem: async (taskId, itemData) => {
          // Store previous state for potential rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Create optimistic update with temporary ID
          const tempId = `temp-${Date.now()}`;
          const newChecklistItem = {
            _id: tempId,
            text: itemData.text,
            isCompleted: itemData.isCompleted || false,
            createdAt: new Date().toISOString()
          };

          // Update the task optimistically
          const updatedTask = {
            ...task,
            checklist: [...(task.checklist || []), newChecklistItem]
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            const response = await api.post(`/tasks/${taskId}/checklist`, itemData);
            const serverItem = response.data?.checklistItem || response.checklistItem;

            // Update with server response
            set(state => {
              // Only update if we still have the same task loaded
              if (state.currentTask?._id !== taskId) {
                return { isLoading: false };
              }

              // Replace the temporary item with the real one from server
              const updatedChecklist = state.currentTask.checklist.map(item =>
                item._id === tempId ? serverItem : item
              );

              return {
                currentTask: {
                  ...state.currentTask,
                  checklist: updatedChecklist
                },
                isLoading: false
              };
            });

            return serverItem;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to add checklist item',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Toggle checklist item
        toggleTaskChecklistItem: async (taskId, itemId) => {
          // Store previous state for potential rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Find the checklist item
          const checklistItem = task.checklist?.find(item => item._id === itemId);
          if (!checklistItem) {
            const error = new Error('Checklist item not found');
            set({ error: error.message });
            throw error;
          }

          // Update the checklist item optimistically
          const updatedChecklist = (task.checklist || []).map(item =>
            item._id === itemId ? { ...item, isCompleted: !item.isCompleted } : item
          );

          // Update the task
          const updatedTask = {
            ...task,
            checklist: updatedChecklist
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            const response = await api.put(`/tasks/${taskId}/checklist/${itemId}`);
            const serverItem = response.data?.checklistItem || response.checklistItem;

            // Update with server response
            set(state => {
              // Only update if we still have the same task loaded
              if (state.currentTask?._id !== taskId) {
                return { isLoading: false };
              }

              // Update the item with server data
              const updatedChecklist = state.currentTask.checklist.map(item =>
                item._id === itemId ? serverItem : item
              );

              return {
                currentTask: {
                  ...state.currentTask,
                  checklist: updatedChecklist
                },
                isLoading: false
              };
            });

            return serverItem;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to toggle checklist item',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Remove checklist item
        removeTaskChecklistItem: async (taskId, itemId) => {
          // Store previous state for potential rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Update the checklist optimistically
          const updatedChecklist = (task.checklist || []).filter(item => item._id !== itemId);

          // Update the task
          const updatedTask = {
            ...task,
            checklist: updatedChecklist
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            await api.delete(`/tasks/${taskId}/checklist/${itemId}`);

            set({ isLoading: false });
            return true;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to remove checklist item',
              isLoading: false
            });

            // Return false instead of throwing
            return false;
          }
        },

        // Delete task
// Fixed deleteTask function
deleteTask: async (taskId) => {
  // Store previous state for rollback
  const previousState = {
    tasks: [...get().tasks],
    projectTasks: { ...get().projectTasks },
    myTasks: [...get().myTasks],
    currentTask: get().currentTask,
    totalTasks: get().totalTasks,
    sectionPagination: { ...get().sectionPagination }
  };

  // Fixed task lookup - check in all collections
  let task = null;
  
  // 1. Check in the main tasks array
  task = get().tasks.find(t => t._id === taskId);
  
  // 2. Check if it's the current task
  if (!task && get().currentTask && get().currentTask._id === taskId) {
    task = get().currentTask;
  }
  
  // 3. Check in all project tasks (important for Kanban view)
  if (!task) {
    Object.values(get().projectTasks).forEach(projectTaskList => {
      const foundTask = projectTaskList.find(t => t._id === taskId);
      if (foundTask) {
        task = foundTask;
      }
    });
  }
  
  // 4. Check in myTasks as well
  if (!task) {
    task = get().myTasks.find(t => t._id === taskId);
  }

  if (!task) {
    const error = new Error('Task not found in state');
    set({ error: error.message });
    console.error('Task not found:', taskId, 'Current state:', {
      tasksCount: get().tasks.length,
      projectTasksIds: Object.keys(get().projectTasks),
      myTasksCount: get().myTasks.length
    });
    throw error;
  }

  const projectId = task.projectId?._id || task.projectId;
  const sectionId = task.sectionId;

  // Optimistically update UI
  set(state => {
    // Update section pagination count
    const newSectionPagination = { ...state.sectionPagination };
    if (projectId && newSectionPagination[projectId]) {
      const projectPagination = { ...newSectionPagination[projectId] };
      
      // Update count for the task's section (or null for "no section")
      const sectionKey = sectionId || null;
      if (projectPagination[sectionKey]) {
        projectPagination[sectionKey] = {
          ...projectPagination[sectionKey],
          total: Math.max(0, (projectPagination[sectionKey].total || 0) - 1)
        };
      }
      
      newSectionPagination[projectId] = projectPagination;
    }
    
    return {
      tasks: state.tasks.filter(t => t._id !== taskId),
      myTasks: state.myTasks.filter(t => t._id !== taskId),
      projectTasks: {
        ...state.projectTasks,
        [projectId]: state.projectTasks[projectId]?.filter(t => t._id !== taskId) || []
      },
      currentTask: state.currentTask?._id === taskId ? null : state.currentTask,
      totalTasks: Math.max(0, state.totalTasks - 1),
      sectionPagination: newSectionPagination,
      isLoading: true
    };
  });

  try {
    await taskService.deleteTask(taskId);

    // Invalidate cache for related endpoints if API cache function exists
    try {
      if (typeof api !== 'undefined' && typeof api.invalidateCache === 'function') {
        api.invalidateCache('/tasks');
        api.invalidateCache(`/tasks/${taskId}`);
        if (projectId) {
          api.invalidateCache(`/projects/${projectId}/tasks`);
        }
        if (sectionId) {
          api.invalidateCache(`/sections/${sectionId}/tasks`);
        }
      }
    } catch (cacheError) {
      console.warn('Cache invalidation failed:', cacheError);
    }

    set({ isLoading: false });
    return true;
  } catch (error) {
    // Rollback on error
    set({
      tasks: previousState.tasks,
      myTasks: previousState.myTasks,
      projectTasks: previousState.projectTasks,
      currentTask: previousState.currentTask,
      totalTasks: previousState.totalTasks,
      sectionPagination: previousState.sectionPagination,
      error: error.message || 'Failed to delete task',
      isLoading: false
    });

    // Return false instead of throwing
    return false;
  }
},
        // Update task priority
        updateTaskPriority: async (taskId, priority) => {
          // Use the updateTask method to handle this update
          return get().updateTask(taskId, { priority });
        },

        // Update task assignee
        updateTaskAssignee: async (taskId, assignedTo) => {
          // Use the updateTask method to handle this update
          return get().updateTask(taskId, { assignedTo });
        },

        // Add task checklist item
        addTaskChecklist: async (taskId, checklistItem) => {
          // Store previous state for rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Create optimistic update with temporary ID
          const tempId = `temp-${Date.now()}`;
          const newChecklistItem = {
            _id: tempId,
            text: checklistItem.text,
            isCompleted: checklistItem.isCompleted || false,
            createdAt: new Date().toISOString()
          };

          // Update the task optimistically
          const updatedTask = {
            ...task,
            checklist: [...(task.checklist || []), newChecklistItem]
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            const response = await api.post(`/tasks/${taskId}/checklist`, checklistItem);
            const serverItem = response.checklistItem || response.data?.checklistItem;

            // Update with server response
            set(state => {
              // Only update if we still have the same task loaded
              if (state.currentTask?._id !== taskId) {
                return { isLoading: false };
              }

              // Replace the temporary item with the real one from server
              const updatedChecklist = state.currentTask.checklist.map(item =>
                item._id === tempId ? serverItem : item
              );

              return {
                currentTask: {
                  ...state.currentTask,
                  checklist: updatedChecklist
                },
                isLoading: false
              };
            });

            return serverItem;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to add checklist item',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Update task checklist item
        updateTaskChecklistItem: async (taskId, itemId, updates) => {
          // Store previous state for rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Update the checklist item optimistically
          const updatedChecklist = (task.checklist || []).map(item =>
            item._id === itemId ? { ...item, ...updates } : item
          );

          // Update the task
          const updatedTask = {
            ...task,
            checklist: updatedChecklist
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            const response = await api.put(`/tasks/${taskId}/checklist/${itemId}`, updates);

            // Update state after successful API call
            set({ isLoading: false });

            return response.checklistItem || response.data?.checklistItem;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to update checklist item',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Delete task checklist item
        deleteTaskChecklistItem: async (taskId, itemId) => {
          // Store previous state for rollback
          const previousState = {
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          // Find the task
          const task = get().currentTask?._id === taskId
            ? get().currentTask
            : get().tasks.find(t => t._id === taskId);

          if (!task) {
            const error = new Error('Task not found in state');
            set({ error: error.message });
            throw error;
          }

          // Remove the checklist item optimistically
          const updatedChecklist = (task.checklist || []).filter(item => item._id !== itemId);

          // Update the task
          const updatedTask = {
            ...task,
            checklist: updatedChecklist
          };

          // Apply optimistic update
          set(state => {
            // Only update currentTask if it's the one being modified
            const newCurrentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              currentTask: newCurrentTask,
              isLoading: true
            };
          });

          try {
            // Make API call
            await api.delete(`/tasks/${taskId}/checklist/${itemId}`);

            // Update state after successful API call
            set({ isLoading: false });

            return true;
          } catch (error) {
            // Rollback on error
            set({
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to delete checklist item',
              isLoading: false
            });

            // Return false instead of throwing
            return false;
          }
        },

        // Helper function to update a task across all state arrays
        updateTaskInAllStates: (taskId, updateFn) => {
          set(state => {
            // Helper to update task in an array
            const updateTaskInArray = (tasks) => {
              if (!tasks) return tasks;
              return tasks.map(task => 
                task._id === taskId ? updateFn(task) : task
              );
            };

            // Update in all possible locations
            const newState = {
              tasks: updateTaskInArray(state.tasks),
              myTasks: updateTaskInArray(state.myTasks),
              projectTasks: { ...state.projectTasks }
            };

            // Update in projectTasks (which is an object keyed by projectId)
            Object.keys(newState.projectTasks).forEach(projectId => {
              if (Array.isArray(newState.projectTasks[projectId])) {
                newState.projectTasks[projectId] = updateTaskInArray(newState.projectTasks[projectId]);
              }
            });

            // Update sections if they contain tasks
            if (state.sections) {
              newState.sections = { ...state.sections };
              Object.keys(newState.sections).forEach(projectId => {
                if (newState.sections[projectId]?.sectionsWithTasks) {
                  newState.sections[projectId] = {
                    ...newState.sections[projectId],
                    sectionsWithTasks: newState.sections[projectId].sectionsWithTasks.map(section => ({
                      ...section,
                      tasks: updateTaskInArray(section.tasks)
                    }))
                  };
                }
                // Also check unsectioned tasks
                if (newState.sections[projectId]?.unsectionedTasks) {
                  newState.sections[projectId] = {
                    ...newState.sections[projectId],
                    unsectionedTasks: updateTaskInArray(newState.sections[projectId].unsectionedTasks)
                  };
                }
              });
            }

            // Update current task if it matches
            if (state.currentTask && state.currentTask._id === taskId) {
              newState.currentTask = updateFn(state.currentTask);
            }

            return newState;
          });
        },

        // Get subtasks for a task
        getTaskSubtasks: async (taskId) => {
          if (!taskId) {
            set({ error: 'Task ID is required' });
            return []; // Return empty array instead of throwing
          }

          set({ isLoading: true, criticalError: null });

          try {
            // Call the API to get subtasks
            const response = await taskService.getTaskSubtasks(taskId);
            console.log("Fetched subtasks:", response);

            // Update state with the subtasks
            set(state => ({
              taskSubtasks: {
                ...state.taskSubtasks,
                [taskId]: response.data?.subtasks || []
              },
              isLoading: false
            }));

            return response.data?.subtasks || [];
          } catch (error) {
            set({
              error: error.message || 'Failed to fetch task subtasks',
              isLoading: false
            });

            // Return empty array instead of throwing
            return [];
          }
        },

        // Create a subtask for a parent task
        createSubtask: async (taskId, subtaskData) => {
          if (!taskId) {
            set({ error: 'Task ID is required' });
            return null; // Return null instead of throwing
          }
          if (!subtaskData) {
            set({ error: 'Subtask data is required' });
            return null; // Return null instead of throwing
          }

          set({ isLoading: true, criticalError: null });

          try {
            // Call the API to create a subtask
            const response = await taskService.createSubtask(taskId, subtaskData);
            console.log("Created subtask:", response);

            // Extract the new subtask
            const newSubtask = response.data?.subtask || response.data;

            // Find the parent task to get its section and project info
            const state = get();
            let parentTask = null;
            
            // Search for parent task in all possible locations
            if (state.currentTask && state.currentTask._id === taskId) {
              parentTask = state.currentTask;
            } else {
              // Search in projectTasks
              for (const projectId in state.projectTasks) {
                const task = state.projectTasks[projectId]?.find(t => t._id === taskId);
                if (task) {
                  parentTask = task;
                  break;
                }
              }
              // Search in sections
              if (!parentTask && state.sections) {
                for (const projectId in state.sections) {
                  const sections = state.sections[projectId]?.sectionsWithTasks;
                  if (sections) {
                    for (const section of sections) {
                      const task = section.tasks?.find(t => t._id === taskId);
                      if (task) {
                        parentTask = task;
                        break;
                      }
                    }
                  }
                  // Check unsectioned tasks
                  if (!parentTask) {
                    const task = state.sections[projectId]?.unsectionedTasks?.find(t => t._id === taskId);
                    if (task) {
                      parentTask = task;
                    }
                  }
                }
              }
            }

            // Update state with the new subtask
            set(state => {
              const currentSubtasks = state.taskSubtasks[taskId] || [];
              const newState = {
                taskSubtasks: {
                  ...state.taskSubtasks,
                  [taskId]: [...currentSubtasks, newSubtask]
                },
                isLoading: false
              };

              // Add the subtask to the main task arrays
              if (parentTask) {
                const projectId = parentTask.projectId?._id || parentTask.projectId;
                const sectionId = parentTask.sectionId?._id || parentTask.sectionId;
                
                // Update section pagination count
                const newSectionPagination = { ...state.sectionPagination };
                if (projectId && newSectionPagination[projectId]) {
                  const projectPagination = { ...newSectionPagination[projectId] };
                  const sectionKey = sectionId || null;
                  
                  if (projectPagination[sectionKey]) {
                    projectPagination[sectionKey] = {
                      ...projectPagination[sectionKey],
                      total: (projectPagination[sectionKey].total || 0) + 1
                    };
                  }
                  
                  newSectionPagination[projectId] = projectPagination;
                }
                newState.sectionPagination = newSectionPagination;

                // Add to projectTasks
                if (state.projectTasks[projectId]) {
                  newState.projectTasks = {
                    ...state.projectTasks,
                    [projectId]: [...state.projectTasks[projectId], newSubtask]
                  };
                }

                // Add to sections
                if (state.sections && state.sections[projectId]) {
                  newState.sections = { ...state.sections };
                  newState.sections[projectId] = { ...state.sections[projectId] };
                  
                  if (sectionId && state.sections[projectId].sectionsWithTasks) {
                    // Add to the specific section
                    newState.sections[projectId].sectionsWithTasks = state.sections[projectId].sectionsWithTasks.map(section => {
                      if (section._id === sectionId) {
                        return {
                          ...section,
                          tasks: [...(section.tasks || []), newSubtask]
                        };
                      }
                      return section;
                    });
                  } else if (!sectionId && state.sections[projectId].unsectionedTasks) {
                    // Add to unsectioned tasks
                    newState.sections[projectId].unsectionedTasks = [
                      ...state.sections[projectId].unsectionedTasks,
                      newSubtask
                    ];
                  }
                }

                // Add to tasks array if it exists
                if (state.tasks) {
                  newState.tasks = [...state.tasks, newSubtask];
                }
              }

              return newState;
            });

            // Update the parent task's subtasks array across all states
            get().updateTaskInAllStates(taskId, (task) => ({
              ...task,
              subtasks: [...(task.subtasks || []), newSubtask]
            }));

            return newSubtask;
          } catch (error) {
            set({
              error: error.message || 'Failed to create subtask',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Toggle subtask completion status
        toggleSubtaskStatus: async (parentTaskId, subtaskId) => {
          if (!parentTaskId || !subtaskId) {
            set({ error: 'Parent task ID and subtask ID are required' });
            return null; // Return null instead of throwing
          }

          // Store previous state for rollback
          const previousState = { ...get().taskSubtasks };

          // Find the subtask
          const subtasks = get().taskSubtasks[parentTaskId] || [];
          const subtask = subtasks.find(s => s._id === subtaskId);

          if (!subtask) {
            set({ error: 'Subtask not found' });
            return null; // Return null instead of throwing
          }

          // Optimistically update the UI
          const newStatus = subtask.status === 'done' ? 'to_do' : 'done';

          set(state => {
            const updatedSubtasks = state.taskSubtasks[parentTaskId].map(s =>
              s._id === subtaskId ? { ...s, status: newStatus } : s
            );

            return {
              taskSubtasks: {
                ...state.taskSubtasks,
                [parentTaskId]: updatedSubtasks
              },
              isLoading: true
            };
          });

          try {
            // Call the API to update the subtask
            const response = await taskService.updateTask(subtaskId, { status: newStatus });
            console.log("Toggled subtask status:", response);

            const updatedSubtask = response.data?.task || response.data;

            // Update the subtask in all task arrays
            set(state => {
              const newState = { isLoading: false };

              // Helper function to update the subtask in an array
              const updateSubtaskInArray = (tasks) => {
                if (!tasks) return tasks;
                return tasks.map(task => 
                  task._id === subtaskId ? { ...task, status: newStatus } : task
                );
              };

              // Update in projectTasks
              if (state.projectTasks) {
                newState.projectTasks = {};
                Object.keys(state.projectTasks).forEach(projectId => {
                  newState.projectTasks[projectId] = updateSubtaskInArray(state.projectTasks[projectId]);
                });
              }

              // Update in sections
              if (state.sections) {
                newState.sections = {};
                Object.keys(state.sections).forEach(projectId => {
                  if (state.sections[projectId]) {
                    newState.sections[projectId] = { ...state.sections[projectId] };
                    
                    // Update in sectioned tasks
                    if (state.sections[projectId].sectionsWithTasks) {
                      newState.sections[projectId].sectionsWithTasks = state.sections[projectId].sectionsWithTasks.map(section => ({
                        ...section,
                        tasks: updateSubtaskInArray(section.tasks)
                      }));
                    }
                    
                    // Update in unsectioned tasks
                    if (state.sections[projectId].unsectionedTasks) {
                      newState.sections[projectId].unsectionedTasks = updateSubtaskInArray(state.sections[projectId].unsectionedTasks);
                    }
                  }
                });
              }

              // Update in tasks array
              if (state.tasks) {
                newState.tasks = updateSubtaskInArray(state.tasks);
              }

              // Update in myTasks
              if (state.myTasks) {
                newState.myTasks = updateSubtaskInArray(state.myTasks);
              }

              return newState;
            });

            // Update the subtask within parent task's subtasks array across all states
            get().updateTaskInAllStates(parentTaskId, (task) => ({
              ...task,
              subtasks: (task.subtasks || []).map(s => {
                // Handle both object and string references
                if (typeof s === 'string') {
                  return s === subtaskId ? updatedSubtask : s;
                } else if (s._id === subtaskId) {
                  return { ...s, status: newStatus };
                }
                return s;
              })
            }));

            return updatedSubtask;
          } catch (error) {
            // Rollback to previous state on error
            set({
              taskSubtasks: previousState,
              error: error.message || 'Failed to update subtask status',
              isLoading: false
            });

            // Return null instead of throwing
            return null;
          }
        },

        // Remove a subtask
        removeSubtask: async (parentTaskId, subtaskId) => {
          if (!parentTaskId || !subtaskId) {
            const error = new Error('Parent task ID and subtask ID are required');
            set({ error: error.message });
            throw error;
          }

          // Store previous state for rollback
          const previousState = { ...get().taskSubtasks };

          // Optimistically update the UI
          set(state => {
            const updatedSubtasks = state.taskSubtasks[parentTaskId].filter(s => s._id !== subtaskId);

            return {
              taskSubtasks: {
                ...state.taskSubtasks,
                [parentTaskId]: updatedSubtasks
              },
              isLoading: true
            };
          });

          try {
            // Call the API to remove the subtask
            await taskService.removeSubtask(parentTaskId, subtaskId);

            // Remove the subtask from all task arrays
            set(state => {
              const newState = { isLoading: false };

              // Helper function to filter out the subtask from an array
              const filterOutSubtask = (tasks) => {
                if (!tasks) return tasks;
                return tasks.filter(task => task._id !== subtaskId);
              };

              // Remove from projectTasks
              if (state.projectTasks) {
                newState.projectTasks = {};
                Object.keys(state.projectTasks).forEach(projectId => {
                  newState.projectTasks[projectId] = filterOutSubtask(state.projectTasks[projectId]);
                });
              }

              // Remove from sections
              if (state.sections) {
                newState.sections = {};
                Object.keys(state.sections).forEach(projectId => {
                  if (state.sections[projectId]) {
                    newState.sections[projectId] = { ...state.sections[projectId] };
                    
                    // Remove from sectioned tasks
                    if (state.sections[projectId].sectionsWithTasks) {
                      newState.sections[projectId].sectionsWithTasks = state.sections[projectId].sectionsWithTasks.map(section => ({
                        ...section,
                        tasks: filterOutSubtask(section.tasks)
                      }));
                    }
                    
                    // Remove from unsectioned tasks
                    if (state.sections[projectId].unsectionedTasks) {
                      newState.sections[projectId].unsectionedTasks = filterOutSubtask(state.sections[projectId].unsectionedTasks);
                    }
                  }
                });
              }

              // Remove from tasks array
              if (state.tasks) {
                newState.tasks = filterOutSubtask(state.tasks);
              }

              // Remove from myTasks
              if (state.myTasks) {
                newState.myTasks = filterOutSubtask(state.myTasks);
              }

              return newState;
            });

            // Update the parent task's subtasks array across all states
            get().updateTaskInAllStates(parentTaskId, (task) => ({
              ...task,
              subtasks: (task.subtasks || []).filter(s => 
                typeof s === 'string' ? s !== subtaskId : s._id !== subtaskId
              )
            }));

            return true;
          } catch (error) {
            // Rollback to previous state on error
            set({
              taskSubtasks: previousState,
              error: error.message || 'Failed to remove subtask',
              isLoading: false
            });

            // Return false instead of throwing
            return false;
          }
        },

        // Clear current task
        clearCurrentTask: () => {
          set({ currentTask: null });
        },

        // Clear current section
        clearCurrentSection: () => {
          set({ currentSection: null });
        },
        // Enhanced taskStore functions for content integration
        // This file should be merged into the existing taskStore.js file


        /**
         * Create a task with content
         * @param {Object} data - Combined data with taskData and contentData
         * @returns {Promise<Object>} - The created task with content
         */
        // createTaskWithContent: async (data) => {
        //   try {
        //     set({ isLoading: true, error: null });

        //     const response = await createTaskWithContent(data);

        //     // Update the tasks in state
        //     const newTask = response.data.task;

        //     // Add to various task collections in state
        //     set(state => {
        //       // Update the general tasks collection
        //       const updatedTasks = [newTask, ...state.tasks];

        //       // Update project tasks if this task belongs to a project
        //       const projectId = newTask.projectId?._id || newTask.projectId;
        //       const updatedProjectTasks = { ...state.projectTasks };

        //       if (projectId && state.projectTasks[projectId]) {
        //         updatedProjectTasks[projectId] = [newTask, ...state.projectTasks[projectId]];
        //       }

        //       // Update my tasks if this task is assigned to the current user
        //       const userId = state.user?.id;
        //       const updatedMyTasks = state.myTasks.slice();

        //       if (userId && (newTask.assignedTo?._id === userId || newTask.assignedTo === userId)) {
        //         updatedMyTasks.unshift(newTask);
        //       }

        //       return {
        //         tasks: updatedTasks,
        //         projectTasks: updatedProjectTasks,
        //         myTasks: updatedMyTasks,
        //         isLoading: false
        //       };
        //     });

        //     return response.data.task;
        //   } catch (error) {
        //     set({ 
        //       isLoading: false, 
        //       error: error.message || 'Failed to create task with content'
        //     });
        //     throw error;
        //   }
        // },

        // createTaskWithContentAndFiles: async (taskData, contentData, tempAssets = [], tempReferences = []) => {
        //   try {
        //     set({ isLoading: true, error: null });

        //     // Step 1: Create task with content (without files)
        //     const cleanContentData = { ...contentData };

        //     // Remove file objects that can't be serialized in JSON
        //     delete cleanContentData.assets;
        //     delete cleanContentData.references;

        //     // Create the task+content
        //     const response = await taskService.createTaskWithContent({
        //       taskData,
        //       contentData: cleanContentData
        //     });

        //     // Get the created task and extract content ID
        //     const task = response.data.task;
        //     const contentId = task.content._id || task.content;

        //     if (!contentId) {
        //       throw new Error('Content ID not found in created task');
        //     }

        //     // Step 2: Upload files for the content
        //     if ((tempAssets && tempAssets.length > 0) || (tempReferences && tempReferences.length > 0)) {
        //       // Import the file upload utility
        //       const { uploadContentFiles } = await import('../utils/fileUploadUtils');

        //       try {
        //         // Upload assets and references
        //         await uploadContentFiles(contentId, tempAssets, tempReferences);
        //       } catch (uploadError) {
        //         console.error('Error uploading files:', uploadError);
        //         // Continue with task creation even if uploads fail
        //       }
        //     }

        //     // Refresh task to get updated content (optional)
        //     if (get().fetchTask) {
        //       await get().fetchTask(task._id);
        //     }

        //     set({ isLoading: false });
        //     return task;
        //   } catch (error) {
        //     set({ 
        //       isLoading: false, 
        //       error: error.message || 'Failed to create task with content' 
        //     });
        //     throw error;
        //   }
        // },
        createTaskWithContentAndFiles: async (taskData, contentData, tempAssets = [], tempReferences = []) => {
          try {
            set({ isLoading: true, criticalError: null });

            // Extract grid assignment metadata and remove from API data
            const gridAssignment = taskData._gridAssignment;
            const apiTaskData = { ...taskData };
            delete apiTaskData._gridAssignment;
            
            console.log('Creating task with content and grid assignment:', { 
              hasGridAssignment: !!gridAssignment, 
              gridAssignment,
              taskTitle: apiTaskData.title 
            });

            // Step 1: Create task with content (without files)
            const cleanContentData = { ...contentData };

            // Remove file objects that can't be serialized in JSON
            delete cleanContentData.assets;
            delete cleanContentData.references;

            // Create the task+content
            const response = await taskService.createTaskWithContent({
              taskData: apiTaskData,
              contentData: cleanContentData
            });

            // Get the created task and extract content ID
            const newTask = response.data.task;
            const contentId = newTask.content?._id || (typeof newTask.content === 'string' ? newTask.content : null);

            if (!contentId) {
              throw new Error('Content ID not found in created task');
            }

            // Step 2: Upload files for the content
            if ((tempAssets && tempAssets.length > 0) || (tempReferences && tempReferences.length > 0)) {
              try {
                await uploadContentFiles(contentId, tempAssets, tempReferences);
              } catch (uploadError) {
                console.error('Error uploading files:', uploadError);
              }
            }

            // Step 3: Update all local state collections
            set(state => {
              // Update tasks list
              const updatedTasks = [newTask, ...state.tasks];

              // Update project tasks
              const projectId = apiTaskData.projectId;
              let updatedProjectTasks = { ...state.projectTasks };

              if (projectId && state.projectTasks[projectId]) {
                updatedProjectTasks[projectId] = [newTask, ...state.projectTasks[projectId]];
              }

              // Update my tasks if this task is assigned to the current user
              let updatedMyTasks = [...state.myTasks];
              if (apiTaskData.assignedTo && apiTaskData.assignedTo === state.userId) {
                updatedMyTasks = [newTask, ...state.myTasks];
              }

              return {
                tasks: updatedTasks,
                projectTasks: updatedProjectTasks,
                myTasks: updatedMyTasks,
                currentTask: newTask, // Set as current task
                isLoading: false
              };
            });

            // Handle grid assignment if metadata is present
            if (gridAssignment && gridAssignment.autoAssign) {
              console.log('Auto-assigning task with content to grid:', { taskId: newTask._id, gridAssignment });
              
              // Import grid service and assign task to grid
              try {
                const gridService = await import('../services/gridService');
                const assignResponse = await gridService.default.assignTask(
                  gridAssignment.gridId, 
                  newTask._id, 
                  gridAssignment.row, 
                  gridAssignment.column
                );
                console.log('Task with content successfully auto-assigned to grid:', assignResponse);
                
                // Update the newly created task with grid assignment info
                newTask.gridAssignments = [{
                  gridTemplateId: gridAssignment.gridId,
                  cellPosition: {
                    row: gridAssignment.row,
                    column: gridAssignment.column
                  }
                }];
              } catch (gridError) {
                console.error('Failed to auto-assign task to grid:', gridError);
                // Don't throw - task was still created successfully
              }
            }

            return newTask;
          } catch (error) {
            set({
              isLoading: false,
              error: error.message || 'Failed to create task with content'
            });
            throw error;
          }
        },

        updateTaskWithContentAndFiles: async (taskId, taskData, contentData, tempAssets = [], tempReferences = []) => {
          // Store previous state for rollback
          const previousState = {
            tasks: [...get()?.tasks],
            projectTasks: { ...get()?.projectTasks },
            myTasks: [...get()?.myTasks],
            currentTask: get().currentTask ? { ...get().currentTask } : null
          };

          try {
            set({ isLoading: true, criticalError: null });

            // Find the task in our state
            const task = get()?.currentTask?._id === taskId
              ? get().currentTask
              : get().tasks.find(t => t._id === taskId);

            if (!task) {
              throw new Error('Task not found in state');
            }

            // Determine which collections this task belongs to
            const projectId = task.projectId?._id || task.projectId;
            const isInProjectTasks = projectId && get().projectTasks[projectId]?.some(t => t._id === taskId);
            const isInMyTasks = get().myTasks.some(t => t._id === taskId);

            // Step 1: Update task data
            const taskResponse = await taskService.updateTask(taskId, taskData);
            let updatedTask = taskResponse.data?.task || taskResponse.data || taskResponse;

            // Step 2: Handle content
            const contentId = contentData._id;

            if (contentId) {
              // Update existing content
              const cleanContentData = { ...contentData };
              delete cleanContentData.assets;
              delete cleanContentData.references;

              await contentService.updateContent(contentId, cleanContentData);

              // Step 3: Upload files for the content
              if ((tempAssets && tempAssets.length > 0) || (tempReferences && tempReferences.length > 0)) {
                try {
                  await uploadContentFiles(contentId, tempAssets, tempReferences);
                } catch (uploadError) {
                  console.error('Error uploading files:', uploadError);
                }
              }
            } else {
              // Create new content and link to task
              const createResponse = await taskService.createTaskWithContent({
                taskId,
                contentData
              });

              const newContentId = createResponse.data.content?._id;

              if (newContentId && ((tempAssets && tempAssets.length > 0) || (tempReferences && tempReferences.length > 0))) {
                try {
                  await uploadContentFiles(newContentId, tempAssets, tempReferences);
                } catch (uploadError) {
                  console.error('Error uploading files:', uploadError);
                }
              }
            }

            // Get the updated task with refreshed content
            const refreshResponse = await get().fetchTask(taskId);
            updatedTask = refreshResponse || updatedTask;

            // Update all state collections
            set(state => {
              // Update tasks array
              const newTasks = state.tasks.map(t => t._id === taskId ? updatedTask : t);

              // Update my tasks array
              const newMyTasks = isInMyTasks
                ? state.myTasks.map(t => t._id === taskId ? updatedTask : t)
                : state.myTasks;

              // Update project tasks
              let newProjectTasks = { ...state.projectTasks };
              if (isInProjectTasks) {
                newProjectTasks[projectId] = state.projectTasks[projectId].map(t =>
                  t._id === taskId ? updatedTask : t
                );
              }

              return {
                tasks: newTasks,
                myTasks: newMyTasks,
                projectTasks: newProjectTasks,
                currentTask: state.currentTask?._id === taskId ? updatedTask : state.currentTask,
                isLoading: false
              };
            });

            return updatedTask;
          } catch (error) {
            // Rollback to previous state on error
            set({
              tasks: previousState.tasks,
              myTasks: previousState.myTasks,
              projectTasks: previousState.projectTasks,
              currentTask: previousState.currentTask,
              error: error.message || 'Failed to update task with content',
              isLoading: false
            });

            throw error;
          }
        },
        
        /**
         * Update project tasks after a reposition operation
         * This method is optimized for drag-and-drop operations
         * @param {string} projectId - Project ID
         * @param {Array} updatedTasks - Updated tasks array
         */
        updateProjectTasksAfterReposition: (projectId, updatedTasks) => {
          set(state => ({
            projectTasks: {
              ...state.projectTasks,
              [projectId]: updatedTasks
            },
            // Also update the main tasks array if it contains project tasks
            tasks: state.tasks.map(task => {
              const updatedTask = updatedTasks.find(t => t._id === task._id);
              return updatedTask || task;
            })
          }));
        },
        
        /**
         * Link existing content to a task
         * @param {string} taskId - Task ID
         * @param {string} contentId - Content ID to link
         * @returns {Promise<Object>} - Updated task with content link
         */
        linkContentToTask: async (taskId, contentId) => {
          try {
            set({ isLoading: true, criticalError: null });

            const response = await linkContentToTask(taskId, contentId);

            // Get the updated task
            const updatedTask = response.data.task;

            // Update all task collections in state
            set(state => {
              // Update tasks array
              const tasks = state.tasks.map(task =>
                task._id === taskId ? updatedTask : task
              );

              // Update my tasks array
              const myTasks = state.myTasks.map(task =>
                task._id === taskId ? updatedTask : task
              );

              // Update project tasks if project exists
              const projectId = updatedTask.projectId?._id || updatedTask.projectId;
              const projectTasks = { ...state.projectTasks };

              if (projectId && projectTasks[projectId]) {
                projectTasks[projectId] = projectTasks[projectId].map(task =>
                  task._id === taskId ? updatedTask : task
                );
              }

              // Update current task if it's the one being updated
              const currentTask = state.currentTask?._id === taskId
                ? updatedTask
                : state.currentTask;

              return {
                tasks,
                myTasks,
                projectTasks,
                currentTask,
                isLoading: false
              };
            });

            return updatedTask;
          } catch (error) {
            set({
              isLoading: false,
              error: error.message || 'Failed to link content to task'
            });
            throw error;
          }
        },

        // Real-time update methods for socket events
        /**
         * Update a task in all collections (for real-time updates)
         * @param {string} taskId - Task ID
         * @param {Object} updatedTask - Updated task data
         */
        updateTaskInList: (taskId, updatedTask) => {
          set(state => {
            // Update tasks array
            const tasks = state.tasks.map(task =>
              task._id === taskId ? updatedTask : task
            );

            // Update my tasks array
            const myTasks = state.myTasks.map(task =>
              task._id === taskId ? updatedTask : task
            );

            // Update project tasks
            const projectId = updatedTask.projectId?._id || updatedTask.projectId;
            const projectTasks = { ...state.projectTasks };
            
            Object.keys(projectTasks).forEach(pid => {
              projectTasks[pid] = projectTasks[pid].map(task =>
                task._id === taskId ? updatedTask : task
              );
            });

            // Update current task if it's the one being updated
            const currentTask = state.currentTask?._id === taskId
              ? updatedTask
              : state.currentTask;

            return {
              tasks,
              myTasks,
              projectTasks,
              currentTask
            };
          });
        },

        /**
         * Remove a task from all collections (for real-time updates)
         * @param {string} taskId - Task ID to remove
         */
        removeTaskFromList: (taskId) => {
          set(state => {
            // Remove from tasks array
            const tasks = state.tasks.filter(task => task._id !== taskId);

            // Remove from my tasks array
            const myTasks = state.myTasks.filter(task => task._id !== taskId);

            // Remove from project tasks
            const projectTasks = { ...state.projectTasks };
            Object.keys(projectTasks).forEach(projectId => {
              projectTasks[projectId] = projectTasks[projectId].filter(
                task => task._id !== taskId
              );
            });

            // Clear current task if it's the one being removed
            const currentTask = state.currentTask?._id === taskId
              ? null
              : state.currentTask;

            return {
              tasks,
              myTasks,
              projectTasks,
              currentTask
            };
          });
        },

        /**
         * Add a new task to collections (for real-time updates)
         * @param {Object} newTask - New task data
         */
        addTaskToList: (newTask) => {
          set(state => {
            // Add to tasks array
            const tasks = [newTask, ...state.tasks];

            // Add to my tasks if assigned to current user
            const currentUserId = useAuthStore.getState().user?.id;
            const assignedUserId = newTask.assignedTo?._id || newTask.assignedTo;
            const myTasks = assignedUserId === currentUserId
              ? [newTask, ...state.myTasks]
              : state.myTasks;

            // Add to project tasks if project exists
            const projectId = newTask.projectId?._id || newTask.projectId;
            const projectTasks = { ...state.projectTasks };
            
            if (projectId) {
              if (!projectTasks[projectId]) {
                projectTasks[projectId] = [];
              }
              projectTasks[projectId] = [newTask, ...projectTasks[projectId]];
            }

            return {
              tasks,
              myTasks,
              projectTasks
            };
          });
        },

        /**
         * Get content details for a task
         * @param {string} taskId - Task ID
         * @returns {Promise<Object>} - Content associated with the task
         */
        getTaskContent: async (taskId) => {
          try {
            set({ isLoading: true, criticalError: null });

            const response = await getTaskContent(taskId);

            // Update the current task if it's being viewed
            if (get().currentTask?._id === taskId) {
              set(state => ({
                currentTask: {
                  ...state.currentTask,
                  content: response.data.content
                }
              }));
            }

            set({ isLoading: false });
            return response.data.content;
          } catch (error) {
            set({
              isLoading: false,
              error: error.message || 'Failed to get content for task'
            });
            return null;
          }
        },

        // ========================================
        // GLOBAL CALENDAR METHODS
        // ========================================
        
        /**
         * Fetch global calendar summary (lightweight counts)
         */
        fetchGlobalCalendarSummary: async (params = {}) => {
          try {
            const response = await globalTaskCalendarService.getGlobalCalendarSummary(params);
            set({ globalCalendarSummary: response.data || {} });
            return response.data;
          } catch (error) {
            console.error('Failed to fetch global calendar summary:', error);
            set({ operationError: error.message });
            return {};
          }
        },
        
        /**
         * Fetch tasks for a specific day in global calendar
         */
        fetchGlobalCalendarDayTasks: async (date, params = {}) => {
          try {
            const response = await globalTaskCalendarService.getGlobalCalendarDayTasks(date, params);
            
            set(state => ({
              globalCalendarTasks: {
                ...state.globalCalendarTasks,
                [date]: response.data
              }
            }));
            
            return response.data;
          } catch (error) {
            console.error('Failed to fetch day tasks:', error);
            set({ operationError: error.message });
            return null;
          }
        },
        
        // ========================================
        // GRANULAR UPDATE METHODS FOR REALTIME SYNC
        // ========================================
        
        // Update a task in all collections without API call
        updateTaskInCollections: (task) => {
          const taskId = task._id;
          const projectId = task.projectId?._id || task.projectId;
          const userId = useAuthStore.getState().user?._id;
          const isAssignedToMe = task.assignedTo === userId || task.assignedTo?._id === userId;
          
          set(state => ({
            // Update in tasks array
            tasks: state.tasks.map(t => t._id === taskId ? task : t),
            // Update in myTasks if assigned to current user
            myTasks: isAssignedToMe 
              ? state.myTasks.map(t => t._id === taskId ? task : t)
              : state.myTasks.filter(t => t._id !== taskId),
            // Update in projectTasks
            projectTasks: projectId ? {
              ...state.projectTasks,
              [projectId]: (state.projectTasks[projectId] || []).map(t => 
                t._id === taskId ? task : t
              )
            } : state.projectTasks,
            // Update currentTask if it's the same
            currentTask: state.currentTask?._id === taskId ? task : state.currentTask
          }));
        },
        
        // Remove a task from all collections without API call
        removeTaskFromCollections: (taskId, projectId) => {
          set(state => {
            // Find the task to get its section info before removing
            let taskToRemove = null;
            if (projectId && state.projectTasks[projectId]) {
              taskToRemove = state.projectTasks[projectId].find(t => t._id === taskId);
            }
            if (!taskToRemove) {
              taskToRemove = state.tasks.find(t => t._id === taskId);
            }
            if (!taskToRemove) {
              taskToRemove = state.myTasks.find(t => t._id === taskId);
            }
            
            // Update section pagination count if we found the task
            const newSectionPagination = { ...state.sectionPagination };
            if (taskToRemove && projectId && newSectionPagination[projectId]) {
              const projectPagination = { ...newSectionPagination[projectId] };
              const sectionKey = taskToRemove.sectionId || null;
              
              if (projectPagination[sectionKey]) {
                projectPagination[sectionKey] = {
                  ...projectPagination[sectionKey],
                  total: Math.max(0, (projectPagination[sectionKey].total || 0) - 1)
                };
              }
              
              newSectionPagination[projectId] = projectPagination;
            }
            
            return {
              // Remove from tasks array
              tasks: state.tasks.filter(t => t._id !== taskId),
              // Remove from myTasks
              myTasks: state.myTasks.filter(t => t._id !== taskId),
              // Remove from projectTasks
              projectTasks: projectId ? {
                ...state.projectTasks,
                [projectId]: (state.projectTasks[projectId] || []).filter(t => 
                  t._id !== taskId
                )
              } : state.projectTasks,
              // Clear currentTask if it's the deleted task
              currentTask: state.currentTask?._id === taskId ? null : state.currentTask,
              // Update section pagination
              sectionPagination: newSectionPagination
            };
          });
        },
        
        // Add a task to collections without API call
        addTaskToCollections: (task) => {
          const projectId = task.projectId?._id || task.projectId;
          const userId = useAuthStore.getState().user?._id;
          const isAssignedToMe = task.assignedTo === userId || task.assignedTo?._id === userId;
          
          set(state => ({
            // Add to tasks array (avoid duplicates)
            tasks: state.tasks.some(t => t._id === task._id) 
              ? state.tasks 
              : [task, ...state.tasks],
            // Add to myTasks if assigned to current user
            myTasks: isAssignedToMe && !state.myTasks.some(t => t._id === task._id)
              ? [task, ...state.myTasks] 
              : state.myTasks,
            // Add to projectTasks
            projectTasks: projectId ? {
              ...state.projectTasks,
              [projectId]: state.projectTasks[projectId]?.some(t => t._id === task._id)
                ? state.projectTasks[projectId]
                : [task, ...(state.projectTasks[projectId] || [])]
            } : state.projectTasks
          }));
        },
        
        // Update a section in project without API call
        updateSectionInProject: (projectId, section) => {
          set(state => ({
            projectSections: {
              ...state.projectSections,
              [projectId]: (state.projectSections[projectId] || []).map(s => 
                s._id === section._id ? section : s
              )
            },
            // Update current section if it's the same
            currentSection: state.currentSection?._id === section._id ? section : state.currentSection
          }));
        },
        
        // Remove a section from project without API call
        removeSectionFromProject: (projectId, sectionId) => {
          set(state => {
            // Remove section
            const updatedSections = (state.projectSections[projectId] || [])
              .filter(s => s._id !== sectionId);
            
            // Move tasks from deleted section to unsectioned
            const updatedTasks = (state.projectTasks[projectId] || []).map(task => 
              task.sectionId === sectionId ? { ...task, sectionId: null } : task
            );
            
            // Remove pagination data for the deleted section
            const updatedPagination = { ...state.sectionPagination };
            if (updatedPagination[projectId]) {
              delete updatedPagination[projectId][sectionId];
            }
            
            return {
              projectSections: {
                ...state.projectSections,
                [projectId]: updatedSections
              },
              projectTasks: {
                ...state.projectTasks,
                [projectId]: updatedTasks
              },
              sectionPagination: updatedPagination,
              // Clear current section if it's the deleted one
              currentSection: state.currentSection?._id === sectionId ? null : state.currentSection
            };
          });
        },
        
        // Add a section to project without API call
        addSectionToProject: (projectId, section) => {
          set(state => ({
            projectSections: {
              ...state.projectSections,
              [projectId]: [...(state.projectSections[projectId] || []), section]
                .sort((a, b) => a.orderIndex - b.orderIndex)
            }
          }));
        },

        // Optimistic update: Add grid assignment to task
        updateTaskGridAssignment: (taskId, gridId, row, column) => {
          set(state => {
            const updateTask = (task) => {
              if (task._id !== taskId) return task;

              const newAssignment = {
                gridTemplateId: gridId,
                cellPosition: { row, column }
              };

              // Remove existing assignment for this grid and add new one
              const filteredAssignments = (task.gridAssignments || [])
                .filter(a => a.gridTemplateId !== gridId);

              return {
                ...task,
                gridAssignments: [...filteredAssignments, newAssignment]
              };
            };

            return {
              tasks: state.tasks.map(updateTask),
              myTasks: state.myTasks.map(updateTask),
              gridTasks: {
                ...state.gridTasks,
                ...Object.keys(state.gridTasks).reduce((acc, projectId) => {
                  acc[projectId] = state.gridTasks[projectId].map(updateTask);
                  return acc;
                }, {})
              }
            };
          });
        },

        // Optimistic update: Remove grid assignment from task
        removeTaskGridAssignment: (taskId, gridId) => {
          set(state => {
            const updateTask = (task) => {
              if (task._id !== taskId) return task;

              return {
                ...task,
                gridAssignments: (task.gridAssignments || [])
                  .filter(a => a.gridTemplateId !== gridId)
              };
            };

            return {
              tasks: state.tasks.map(updateTask),
              myTasks: state.myTasks.map(updateTask),
              gridTasks: {
                ...state.gridTasks,
                ...Object.keys(state.gridTasks).reduce((acc, projectId) => {
                  acc[projectId] = state.gridTasks[projectId].map(updateTask);
                  return acc;
                }, {})
              }
            };
          });
        }
      }),
      {
        name: 'task-store',
        // Only persist some fields to avoid issues with large state
        partialize: (state) => ({
          pagination: state.pagination,
          totalTasks: state.totalTasks
        })
      }
    )
  )
);

/**
 * Helper function to update a task in all collections
 * @param {string} taskId - ID of the task to update
 * @param {Function} updateFn - Function that takes a task and returns an updated task
 */
function updateTaskInCollections(taskId, updateFn) {
  const { tasks, myTasks, projectTasks, set } = useTaskStore.getState();

  // Update in tasks list if present
  const updatedTasks = tasks.map(task =>
    task._id === taskId ? updateFn(task) : task
  );

  // Update in myTasks if present
  const updatedMyTasks = myTasks.map(task =>
    task._id === taskId ? updateFn(task) : task
  );

  // Update in projectTasks if present
  const updatedProjectTasks = { ...projectTasks };
  Object.keys(projectTasks).forEach(projectId => {
    if (projectTasks[projectId].some(task => task._id === taskId)) {
      updatedProjectTasks[projectId] = projectTasks[projectId].map(task =>
        task._id === taskId ? updateFn(task) : task
      );
    }
  });

  set({
    tasks: updatedTasks,
    myTasks: updatedMyTasks,
    projectTasks: updatedProjectTasks
  });
}

export default useTaskStore;